import {Dispatch, FC, memo, MouseEvent, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import {
  AssignmentTurnedInOutlined,
  AttachMoneyOutlined as AttachMoneyIcon,
  CheckCircleOutline,
  HourglassEmptyOutlined as HourglassEmptyIcon,
  InfoTwoTone as InfoIcon,
  PublishOutlined,
  SwapHorizOutlined,
} from '@mui/icons-material';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import { Grid } from '@mui/system';
import { flatten, isEmpty, isNil, isNull, maxBy, once, sortBy } from 'lodash';
import uuid from 'react-uuid';
import {
  AllocationName,
  AllocationScenarioValuationModel,
  OPMCalculator,
  ValuationApproachGPC,
  ValuationApproachGPT,
  ValuationApproachWeightModel,
} from 'api';
import { valuationsAction } from 'common/actions';
import { SYSTEM_COLLAPSE, SYSTEM_EXPAND } from 'common/actions/row-groups/types';
import { ALLOCATIONS_FINAL, OPM } from 'common/constants/allocations';
import { CAP_TABLE_CURRENCY_PAGE, FINANCIALS_CURRENCY_PAGE } from 'common/constants/currencyPageTypes';
import { ALLOCATIONS_REFERENCE_TYPE } from 'common/constants/documents';
import { INITIAL, VALIDATED, VALIDATING } from 'common/constants/general';
import { ALLOCATIONS_PAGE_KEY } from 'common/constants/notes';
import { COMPANY_PAGE_ACTIONS } from 'common/constants/pageActions';
import { COMMON_STOCK, CONVERTIBLE_NOTES, PREFERRED_STOCK } from 'common/constants/securityTypes';
import { AUDITOR_VALUE } from 'common/constants/user';
import { UNREALIZED_FIRM_TOTAL_CARD_TITLE, VALUATIONS_SLUG } from 'common/constants/valuations';
import {
  type CompanyPageActionHandler,
  type CompanyPageActionHandlers,
  useCompanyPageAction,
  useCurrencySelector ,
  useFormat,
} from 'common/hooks';
import { rowsGroupsReducer } from 'common/reducers/rowsGroups';
import { useStore } from 'common/store';
import { NewAllocationScenario } from 'common/types/allocation';
import { DialogContextValues } from 'common/types/dialogContext';
import { UseFormatValues } from 'common/types/format/format';
import { EXPORT_MODE_STYLES } from 'common/types/pdfExport/constants';
import { ConfigurationSpreadsheets, ResetParams } from 'common/types/scalarSpreadsheet';
import { LayoutContextValues, UseStoreValues } from 'common/types/store';
import { UnsavedChangesValues } from 'common/types/unsavedChanges';
import {
  NewValuationApproach,
  ValuationApproachAdditionalProperties,
  ValuationByAllocation,
  ValuationsApproach,
} from 'common/types/valuation';
import { Calibration as CalibrationValuationModel } from 'common/types/valuation/valuation';
import {
  Alert,
  AllocationFinalMessage,
  CustomContainer,
  EmptyTableMessage,
  LoadingSection,
  MessageBox,
  Tab,
  TabsNavigation,
} from 'components';
import CurrencyMenu from 'components/CurrencyMenu';
import { ConfirmationDialog } from 'components/Dialogs';
import WidgetDrawer from 'components/Drawer/widget-drawer/WidgetDrawer';
import { WidgetDrawerApplicationContext } from 'components/Drawer/widget-drawer/WidgetDrawer.props';
import { REGULAR_UNIT } from 'components/FeaturedSpreadsheet/constants';
import useWorkbook from 'components/ScalarSpreadsheet/utilities/useWorkbook';
import WorkbookContext from 'components/ScalarSpreadsheet/utilities/WorkbookContext';
import { ConfirmUnsavedChanges } from 'components/Spreadsheet/components';
import {
  OverflowTooltip,
  PrimaryNavigationMenu,
  PrimaryNavigationMenuItem,
  SecondaryNavigationAllocationStatusBadge,
  SecondaryNavigationCollapsibleGroup,
  SecondaryNavigationDailyNavOverrideBadge,
  SecondaryNavigationInfoLabel,
  SecondaryNavigationReadyForAuditBadge,
  SecondaryNavigationScalarOwnerBadge,
  type TertiaryNavigationItem,
} from 'components/ui';
import { DialogContext, LayoutContext } from 'context';
import type { SecondaryNavigationContributionState, TertiaryNavigationState } from 'context/FirmBaseLayoutContext';
import {
  useSecondaryNavigationContributionRegistration,
  useTertiaryNavigationQueryParamState,
  useTertiaryNavigationRegistration,
} from 'context/FirmBaseLayoutContext';
import UnsavedChangesContext from 'context/UnsavedChangesContext';
import { useZoom } from 'context/ZoomContext';
import { useExportSignal, useExportWidgetRelay } from 'features/pdfExport';
import { useQueryParamValue } from 'hooks/useQueryParamValue';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import NewVersionForm from 'pages/Allocation/allocations/components/NewVersionForm';
import Calibration from 'pages/Valuations/approaches/guidelineCalibration/Calibration';
import { PERCENTILE_ROWS } from 'pages/Valuations/approaches/guidelinePublicCompanies/constants';
import { synchronizeAndUpdateApproaches } from 'pages/Valuations/approaches/guidelinePublicCompanies/gpc/config/utilities';
import {
  GPCRowConfig,
  GPCRowTransformer,
  ReverseParsedValuationApproachGPC,
} from 'pages/Valuations/approaches/guidelinePublicCompanies/types';
import { useCombinedGPCComparisonPerfMetricAttrs } from 'pages/Valuations/approaches/guidelinePublicCompanies/utils/useCombinedGPCComparisonPerfMetricAttrs';
import RefreshBacksolveSourceRowsOption from 'pages/Valuations/components/RefreshBacksolveSourceRowsOption/RefreshBacksolveSourceRowsOption';
import RefreshCalibrationOption from 'pages/Valuations/components/RefreshCalibrationOption/RefreshCalibrationOption';
import { useRefreshHelpers } from 'pages/Valuations/components/RefreshGPCOption/hooks';
import RefreshGPCOption from 'pages/Valuations/components/RefreshGPCOption/RefreshGPCOption';
import { PdfExportTargets } from 'pages/Valuations/util/constants';
import {
  GPTRowConfig,
  GPTRowTransformer,
  ReverseParsedValuationApproachGPT,
} from 'pages/ValuationsAllocation/approaches/TransactionComps/types';
import {
  EQUITY_ALLOCATION_KEY,
  MISSING_APPROACHES_FOR_EQUITY_ALLOCATION,
  MISSING_APPROACHES_FOR_EQUITY_ALLOCATION_ID,
} from 'pages/ValuationsAllocation/common/constants/equityAllocation';
import {
  COLUMNS_GROUPS_INITIAL_STATE,
  EQUITY_ALLOCATION_ROWS_GROUPS_INITIAL_STATE,
} from 'pages/ValuationsAllocation/common/constants/valuationApproaches';
import {
  ALLOCATION_SCENARIO_DEFAULT_VALUES,
  CALIBRATION_MODEL_NAME,
  CHANGE_FINANCIAL_STATEMENT_LABEL,
  CHANGE_OWNERSHIP_LABEL,
  CHANGE_TO_SCALAR_VALUATION_LABEL,
  CHANGE_VALUATION_OWNERSHIP_LABEL,
  DEFAULT_VALUES,
  DELETE_APPROACH_LABEL,
  DELETE_CALIBRATION_LABEL,
  DELETE_OPM_CALCULATOR_LABEL,
  DELETE_RANGE_SENSITIVITY_LABEL,
  DELETE_VALUATION_BUTTON_LABEL,
  EDIT_APPROACH_NAME_LABEL,
  EDIT_CALIBRATION_NAME_LABEL,
  EDIT_OPM_CALCULATOR_NAME_LABEL,
  EDIT_RANGE_SENSITIVITY_NAME_LABEL,
  ENTERPRISE_VALUE_LABEL,
  EQUITY_VALUE_LABEL,
  EQUITY_VALUE_OPTION,
  FINANCIAL_STATEMENT_LABEL,
  GIVE_OWNERSHIP_TO_FIRM_LABEL,
  INVALID_CHARACTERS_REGEX,
  LOADING_TIMEOUT_DELAY,
  MAKE_FINAL_ALLOCATION_BUTTON_LABEL,
  OPM_CALCULATIONS_EXPORT_PANEL_ID,
  OPM_CALCULATOR_MODEL_NAME,
  PUBLISH_ALLOCATION_BUTTON_LABEL,
  RANGE_SENSITIVITY_MODEL_NAME,
  READY_FOR_AUDIT,
  REDIRECT_TIMEOUT_DELAY,
  REMOVE_READY_FOR_AUDIT,
  SAVE_VALUATION_BUTTON_LABEL,
  SCALAR_OWNED_DRAFT_MESSAGE,
  SET_AS_READY_FOR_AUDIT,
  VALUATIONS_APPROACH_GPC_PROPERTY,
  VALUATIONS_BACKSOLVE_APPROACH,
  VALUATIONS_CANNOT_DELETE_GPC,
  VALUATIONS_CAP_TABLE_CURRENCY_APPROACHES,
  VALUATIONS_DISCOUNT_CASH_FLOW_APPROACH,
  VALUATIONS_EXTERNAL_VALUATION_APPROACH,
  VALUATIONS_FINAL_VALUATION_LOCKED_MESSAGE,
  VALUATIONS_FUTURE_EXIT_APPROACH,
  VALUATIONS_MISSING_FINANCIAL_STATEMENT_TAGLINE,
  VALUATIONS_MISSING_FINANCIAL_STATEMENT_TITLE,
  VALUATIONS_MUTUAL_FUND_MARKS_APPROACH,
  VALUATIONS_PUBLIC_COMPANIES_APPROACH,
  VALUATIONS_PUBLIC_TRANSACTIONS_APPROACH,
  VALUATIONS_SECONDARY_TRANSACTIONS_APPROACH,
  VALUATIONS_SPECIFIED_SHARE_VALUES_APPROACH,
  VALUATIONS_SPREADSHEET_ENTERPRISE_VALUE_KEY,
  VALUATIONS_SPREADSHEET_TABLE_TERMS,
  VALUATIONS_VALUATION_REFERENCES_MAP,
} from 'pages/ValuationsAllocation/common/constants/valuations';
import {
  AddScenarioApproachDialog,
  ApproachActionOption,
  ApproachDialog,
  ApproachTypes,
  DefaultPanelComponent,
  DeleteApproachDialog,
  DeleteCalibrationDialog,
  DeleteOPMCalculatorDialog,
  DeleteRangeSensitivityDialog,
  InvalidTablesMessage,
  RedirectToFinancialButton,
} from 'pages/ValuationsAllocation/components';
import ValuationMarketDateSelector from 'pages/ValuationsAllocation/components/ValuationMarketDateSelector';
import ValuationOwnershipConfirmation
  from 'pages/ValuationsAllocation/components/ValuationOwnershipConfirmation/ValuationOwnershipConfirmation';
import ValuationVersionSelector from 'pages/ValuationsAllocation/components/ValuationVersionSelector';
import {
  Configuration,
  ConfiguredSpreadsheets,
  EnterpriseValues,
  savePanelNotesEntries,
  useApproachQueryParam,
  useCalibrationNotes,
  useCreateCalibration,
  useCreateOPMCalculator,
  useCreateRangeSensitivity,
  useCreateValuation,
  useDependenciesRecalculation,
  useEquityAllocationInputHandlers,
  useInitializeApproachDependencyGraph,
  useLogInvalidCells,
  usePCMarketDataRefresh,
  useRangeSensitivityDocumentsAndNotes,
  useValuationStatusManagement,
} from 'pages/ValuationsAllocation/hooks';
import {
  areApproachesSynchronized,
  buildCalibrationPanelId,
  calculateEquityValue,
  filterApproaches,
  getApproachTableName,
  getFirmTotal,
  getGenericApproachTypeObject,
  getGPCOptions,
  getInvalidSheetTableName,
  getOPMCalculatorPanelId,
  getSpreadsheetsFromWorkbook,
  getTableNames,
  getWeightedEquityValue,
  isApproachTypeOf,
  sortApproaches,
  toPdfExportTabId,
  updateDCFPeriodsByVersion,
  updatePercentileAndGetConfiguration,
  valuationUsesOPM,
} from 'pages/ValuationsAllocation/util';
import ValuationContext from 'pages/ValuationsAllocation/ValuationContext';
import { useDocuments } from 'services/hooks';
import { useNotes } from 'services/hooks/notes';
import { useCreateNewVersion } from 'services/hooks/valuations';
import { useAllocationValuesStore, useScalarSpreadsheetStore, useValuationsStore } from 'store';
import {
  getAllocationStatus,
  getArrayValue,
  getBooleanValue,
  getNumberValue,
  getObjectValue,
  getSlugValue,
  getStringValue,
  handleCatchError,
  isFalseStrict,
  Logger,
  returnCurrencyFormattedValueOrNull,
  scrollToTop,
} from 'utilities';
import { EquityAllocation, WeightedShareValues } from './allocation';
import { EquityAllocationColumn } from './allocation/EquityAllocation/config';
import { createColumns as createEquityAllocationColumns } from './allocation/EquityAllocation/config/createColumns';
import { customRowConfig as createEquityAllocationRows } from './allocation/EquityAllocation/config/customRowConfig';
import generateEquityAllocationKey from './allocation/EquityAllocation/config/util/generate-equity-allocation-key';
import {
  getEquityAllocationVolatilitySourceOptions
} from './allocation/EquityAllocation/util/getEquityAllocationVolatilitySourceOptions';
import { ValuationSummary } from './approaches';
import OPMCalculatorPanel from './approaches/OPMCalculator';
import RangeSensitivityPanel from './approaches/RangeSensitivity';
import { getRangeSensitivityWorkbookKey } from './approaches/RangeSensitivity/constants';
import { ValuationSummaryCollapsibleColumns } from './approaches/ValuationSummary';
import { ValuationSummaryColumn } from './approaches/ValuationSummary/config';
import { createColumns as createValuationSummaryColumns } from './approaches/ValuationSummary/config/createColumns';
import {
  EQUITY_ALLOCATION_SPREADSHEET_OPM_INPUTS,
  EQUITY_ALLOCATION_SPREADSHEET_PRESENT_EQUITY_VALUE,
} from './common/constants/equityAllocation/sheetAliases';
import { EQUITY_ALLOCATION_SPREADSHEET_TABLE_TERMS } from './common/constants/equityAllocation/sheetConfigs';
import { FIRST_ALLOCATION_SCENARIOS_COLUMN_NUMBER } from './common/constants/equityAllocation/sheetTitles';
import {
  VALUATION_SUMMARY_KEY,
  VALUATION_SUMMARY_LABEL,
} from './common/constants/valuationSummary';
import {
  VALUATION_SUMMARY_SPREADSHEET_WEIGHTED_ENTERPRISE_VALUE_KEY,
  VALUATION_SUMMARY_SPREADSHEET_WEIGHTED_EQUITY_VALUE_KEY,
} from './common/constants/valuationSummary/sheetAliases';
import { VALUATION_SUMMARY_SPREADSHEET_TABLE_TERMS } from './common/constants/valuationSummary/sheetConfigs';
import { ALLOCATION_WEIGHTED_SHARE_VALUES_KEY } from './common/constants/weightedShareValues';
import { ALLOCATION_WEIGHTED_SHARE_VALUES_SPREADSHEET_TABLE_TERMS } from './common/constants/weightedShareValues/sheetConfigs';
import { valuationTabId } from './common/utils/valuationTabId';
import { CreateNewVersionButton } from './components/CreateNewVersionButton';
import { SetReadyForAuditConfirmation } from './components/SetReadyForAuditConfirmation';
import ApproachRegistryContext from './context/ApproachRegistryContext';
import { useApproachDependencyGraph } from './hooks/useApproachDependencyGraph';
import { useApproachStableIds } from './hooks/useApproachStableIds';
import { useFirmTotal } from './hooks/useFirmTotal';
import {
  ChangedCell,
  DiscountedCashFlowProperties,
  File,
  GetIsGPCInApproachParams,
  Note,
  RedirectToNewApproachParams,
  RelatedEntity,
  SecurityIdMapType,
  SlimApproachTable,
  SpreadsheetsNames,
  UpdateMainSpreadsheetsParams,
  ValuationApproachesProps,
  ValuationApproachWithReferences,
  ValuationsAreCellsValidValues,
  ValuationsFormState,
  ValuationsUseWorkbook,
  ValuationWithReferences,
} from './types';
import { getDefaultCompsApproachForDropdown } from './util/getDefaultCompsApproachForDropdown';
import {
  ApproachMenuInfoItem,
  ApproachMenuInfoLabel,
  ApproachMenuInfoSection,
  ApproachMenuInfoValue,
  useStyles,
} from './ValuationApproaches.styles';
import { VALUATION_APPROACHES_TEST_IDS } from './ValuationApproaches.testIds';
import { VALUATION_MARKET_DATE_TEST_IDS } from './ValuationMarketDate.testIds';

// Separate component so ValuationApproaches does not subscribe to FirmBaseLayoutContext,
// which would create a render loop (nav registration updates context → context re-renders
// subscriber → subscriber re-registers → loop).
interface ValuationNavigationRegistrarProps {
  secondaryNavContribution: SecondaryNavigationContributionState | null;
  tertiaryNavState: TertiaryNavigationState | null;
}

const ValuationNavigationRegistrar = memo(({
  secondaryNavContribution,
  tertiaryNavState,
}: ValuationNavigationRegistrarProps) => {
  useTertiaryNavigationRegistration(tertiaryNavState);
  useSecondaryNavigationContributionRegistration(secondaryNavContribution);
  return null;
});

ValuationNavigationRegistrar.displayName = 'ValuationNavigationRegistrar';

const renderApproachMenuInfoItem = (label: string, value: string | null, key: string) => {
  const displayValue = getStringValue(value);

  return (
    <ApproachMenuInfoItem key={key}>
      <ApproachMenuInfoLabel>{label}</ApproachMenuInfoLabel>
      <OverflowTooltip placement="bottom" title={displayValue}>
        <ApproachMenuInfoValue>{displayValue}</ApproachMenuInfoValue>
      </OverflowTooltip>
    </ApproachMenuInfoItem>
  );
};

const getApproachFinancialStatementInfoValue = (financialStatementName: string) => {
  const displayValue = financialStatementName.replace(/^Financial Statement\s*/i, '').trim();

  return displayValue || financialStatementName;
};

const ValuationApproaches: FC<ValuationApproachesProps> = props => {
  const { zoomLevel } = useZoom();
  const {
    financialStatementsList,
    allocationVersion,
    capTableVersions,
    cashTaxRate,
    companyMeasurementDate,
    compGroups,
    displayDecisionAlerts,
    financialFiscalYearPeriods,
    financialPeriods,
    financialPeriodsPerMD,
    financials,
    financialVersions,
    getDisconnectDialog,
    getValuationInfo,
    getVersions,
    isLoadingPublicCompsByDate,
    isUpdatingValuation,
    measurementDate,
    otherFinancialStatements,
    primaryCapTable,
    primaryFinancialStatement,
    publicCompsByDate,
    saveValuation,
    setCashTaxRate,
    setValuation,
    valuation,
    marketDate,
    marketCurrency,
    initiateStockMarketDataRefreshFlow,
    numberOfVersions,
    onOpenMarketDateDialog,
    onOpenRenameVersionDialog,
    versions,
    autoCreateApproach,
  } = props;

  const { id: valuationId, valuations_approaches: valuationApproaches } = getObjectValue(valuation);
  const {
    allocation,
    is_firm_owned: isValuationFirmOwned,
    is_ready_for_audit: isValuationReadyForAudit,
  } = getObjectValue(valuation);
  const {
    allocation_scenarios: allocationScenarios = [],
    id: allocationId,
    holding_companies_allocations: holdingAllocations = [],
    is_daily_nav_override: isDailyNavOverride = false,
  } = getObjectValue(allocation);

  const getInitialAllocationStatus = (allocationVersion: AllocationName) => {
    const { is_final = false, is_published = false } = getObjectValue(allocationVersion);
    return { is_final, is_published };
  };
  const [isCurrentAllocationFinal, setIsCurrentAllocationFinal] = useState(
    getInitialAllocationStatus(allocationVersion).is_final
  );
  const [isCurrentAllocationPublished, setIsCurrentAllocationPublished] = useState(
    getInitialAllocationStatus(allocationVersion).is_published
  );

  // Keep the invalid spreadsheet drawer in sync with tab names that cannot be exported/saved.
  useEffect(() => {
    // Prefer fresh allocation data from valuation when it matches the current version,
    // otherwise fall back to the allocationVersion prop (from the version list).
    // This ensures status updates (e.g. making a version final) are reflected immediately
    // after getValuationInfo() resolves with updated data.
    const allocationMatchesVersion = allocation?.id !== undefined && allocation?.id === allocationVersion?.id;
    const isFinal = allocationMatchesVersion
      ? (allocation?.is_final ?? false)
      : getInitialAllocationStatus(allocationVersion).is_final;
    const isPublished = allocationMatchesVersion
      ? (allocation?.is_published ?? false)
      : getInitialAllocationStatus(allocationVersion).is_published;
    setIsCurrentAllocationFinal(isFinal);
    setIsCurrentAllocationPublished(isPublished);
  }, [allocationVersion, allocation?.is_final, allocation?.is_published, allocation?.id]);

  const [storeValue, dispatch] = useStore() as unknown as UseStoreValues;
  const { companyInfo, user, firmId } = storeValue;
  const { is_staff: isUserStaff } = getObjectValue(user);

  const { showDialog } = useContext(DialogContext) as unknown as DialogContextValues;
  const {
    action: hasUnsavedChanges,
    cleanAction: cleanUnsavedChanges,
    setAction: setUnsavedChanges,
    showDialog: showUnsavedChangesGuard,
  } = useContext(UnsavedChangesContext) as unknown as UnsavedChangesValues;

  const { companyExchangeRate, financialExchangeRate, viewOnlyUser, isExportMode, exportTab } =
    useContext(LayoutContext) as unknown as LayoutContextValues;

  const [approaches, setApproaches] = useState<ValuationsApproach[]>([]);
  const [approachToUpdate, setApproachToUpdate] = useState<ValuationsApproach | null>(null);
  const [arePendingChanges, setArePendingChanges] = useState(false);
  const [areSynchronized, setAreSynchronized] = useState(false);
  const [areTabsSynchronized, setAreTabsSynchronized] = useState(false);
  const [changedCells, setChangedCells] = useState<ChangedCell[]>([]);
  const [collapsibleColumns, setCollapsibleColumns] =
    useState<ValuationSummaryCollapsibleColumns>(COLUMNS_GROUPS_INITIAL_STATE);
  const [currentWeightedEquityValue, setCurrentWeightedEquityValue] = useState<number | null>(null);
  const [deletedApproachesIds, setDeletedApproachesIds] = useState<number[]>([]);
  const [deletedScenariosIds, setDeletedScenariosIds] = useState<number[]>([]);
  const [equityAllocationRowsGroups, setEquityAllocationRowsGroups] = useReducer(
    rowsGroupsReducer,
    EQUITY_ALLOCATION_ROWS_GROUPS_INITIAL_STATE
  );
  const [filesInApproach, setFilesInApproach] = useState<File[]>([]);
  const [firmTotal, setFirmTotal] = useState<number | null>(null);
  const [invalidTables, setInvalidTables] = useState<SlimApproachTable[]>([]);
  const [isChangingFinancialStatement, setIsChangingFinancialStatement] = useState(false);
  const [isCurrentValuationDisabled, setIsCurrentValuationDisabled] = useState(
    isCurrentAllocationFinal || isUpdatingValuation || viewOnlyUser
  );
  const [isEditingApproachName, setIsEditingApproachName] = useState(false);
  const [isEditingCalibrationName, setIsEditingCalibrationName] = useState(false);
  const [isEditingRangeSensitivityName, setIsEditingRangeSensitivityName] = useState(false);
  const [isEditingOPMCalculatorName, setIsEditingOPMCalculatorName] = useState(false);
  const [isLoadingValuationSummary, setIsLoadingValuationSummary] = useState(true);
  const [isUpdatingSpreadsheets, setIsUpdatingSpreadsheets] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLSpanElement | null>(null);
  const [tabMenuAnchorEl, setTabMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [menuTabId, setMenuTabId] = useState<string | null>(null);
  const [saveMenuAnchorEl, setSaveMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [notesInApproach, setNotesInApproach] = useState<Note[]>([]);
  const [openAddApproachDialog, setOpenAddApproachDialog] = useState(false);
  const [openedApproaches, setOpenedApproaches] = useState<Map<string, boolean>>(new Map());
  const [openUpdateApproachDialog, setOpenUpdateApproachDialog] = useState(false);
  const [pageStatus, setPageStatus] = useState(INITIAL);
  const [valuationApproachesTabs, setValuationApproachesTabs] = useState<Tab[]>([]);
  const [openNewVersionDialog, setOpenNewVersionDialog] = useState(false);
  const setCurrentValuation = useValuationsStore(state => state.setCurrentValuation);

  const currentValuationApproaches = useRef(getArrayValue(valuation.valuations_approaches));

  // Prevent duplicate POSTs for the same approach while its note save is still in flight.
  const notesSaveInFlightByPanelRef = useRef<Set<string>>(new Set());
  const classes = useStyles();

  // Discounted Cash Flow
  const discountedCashFlowProperties = useMemo(
    () =>
      ({
        measurementDate,
        cashTaxRate,
        companyMeasurementDate,
      }) as DiscountedCashFlowProperties,
    [cashTaxRate, companyMeasurementDate, measurementDate]
  );

  // Approach Registry: stable IDs and dependency graph
  const stableIds = useApproachStableIds();
  const dependencyGraph = useApproachDependencyGraph();
  const { register: registerNode } = dependencyGraph;
  const { getOrCreateId } = stableIds;

  const handleOpenUpdateApproachDialog = useCallback(() => setOpenUpdateApproachDialog(true), []);
  const handleMenuClose = useCallback(() => {
    setMenuAnchorEl(null);
    setTabMenuAnchorEl(null);
    setMenuTabId(null);
  }, []);

  // Create Valuation
  const {
    backsolveAttributes,
    configurations,
    enterpriseValues,
    guidelinePublicCompaniesAttributes,
    calibrationAttributes,
    isLoadingAllocationScenariosValues,
    isLoadingBacksolvesEquityValues,
    isLoadingRiskFreeRates,
    isUniformCurrency,
    publicCompaniesAttributes,
    resetConfigurations,
    riskFreeRates,
    securities,
    setEnterpriseValues,
    spreadsheets,
    updateAllocationScenariosValues,
    updateOPMInputs,
    updateSecurities,
    weightedShareValuesNames,
    weightingProbabilities,
    allocationMethodsOptions,
    updateApproachesScenarioMethods,
  } = useCreateValuation({
    financialStatementsList,
    allocationVersion,
    approaches,
    capTable: primaryCapTable,
    capTableVersions,
    compGroups,
    deletedApproachesIds,
    discountedCashFlowProperties,
    financialPeriods,
    financials,
    isValuationDisabled: isCurrentValuationDisabled,
    otherFinancialStatements,
    valuation,
    marketDate,
    marketCurrency,
    holdingAllocations,
    publicCompsByDate,
  });

  const {
    handleDeleteCalibration,
    showUpdateCalibrationNameDialog,
    updateValuationCalibrations,
    setCalibrationToUpdate,
    calibrations,
    setCalibrations,
    calibrationToUpdate,
    setCalibrationTabs,
    calibrationTabs,
  } = useCreateCalibration({
    valuation,
    REDIRECT_TIMEOUT_DELAY,
    setArePendingChanges,
    setValuation,
    setPageStatus,
    handleOpenUpdateApproachDialog,
    setApproachToUpdate,
    handleMenuClose,
    setIsChangingFinancialStatement,
    setIsEditingCalibrationName,
    setIsEditingRangeSensitivityName,
    setIsEditingApproachName,
  });

  const {
    handleDeleteRangeSensitivity,
    showUpdateRangeSensitivityNameDialog,
    updateValuationRangeSensitivities,
    setRangeSensitivityToUpdate,
    rangeSensitivityToUpdate,
    setRangeSensitivityTabs,
    rangeSensitivityTabs,
  } = useCreateRangeSensitivity({
    valuation,
    REDIRECT_TIMEOUT_DELAY,
    setArePendingChanges,
    setValuation,
    setCurrentValuation,
    setPageStatus,
    handleOpenUpdateApproachDialog,
    setApproachToUpdate,
    handleMenuClose,
    setIsChangingFinancialStatement,
    setIsEditingCalibrationName,
    setIsEditingRangeSensitivityName,
    setIsEditingApproachName,
    setCalibrationToUpdate,
  });

  const {
    clearOPMCalculatorPrefillSource,
    handleDeleteOPMCalculator,
    handleOpenOPMCalculator,
    handleUpdateOPMCalculatorInputs,
    handleUpdateOPMCalculatorName,
    showUpdateOPMCalculatorNameDialog,
    updateValuationOPMCalculators,
    setOPMCalculatorToUpdate,
    opmCalculatorToUpdate,
    opmCalculatorPrefillSource,
    setOPMCalculatorTabs,
    opmCalculatorTabs,
    unsavedOPMCalculatorPanelIds,
  } = useCreateOPMCalculator({
    valuation,
    arePendingChanges,
    REDIRECT_TIMEOUT_DELAY,
    setArePendingChanges,
    setValuation,
    setCurrentValuation,
    setPageStatus,
    handleOpenUpdateApproachDialog,
    setApproachToUpdate,
    handleMenuClose,
    setIsChangingFinancialStatement,
    setIsEditingCalibrationName,
    setIsEditingRangeSensitivityName,
    setIsEditingOPMCalculatorName,
    setIsEditingApproachName,
    setCalibrationToUpdate,
    setRangeSensitivityToUpdate,
  });

  const combinedGPCComparisonPerfMetricAttrs = useCombinedGPCComparisonPerfMetricAttrs(
    guidelinePublicCompaniesAttributes
  );

  const {
    setSourceOfChange,
    recalculatedState,
    applyRecalculationToApproaches,
    applyRecalculationToCalibrations,
    setRecalculatedState,
  } = useDependenciesRecalculation({
    stableIds,
    approaches,
    calibrations,
    dependencyGraph,
    valuationId,
  });

  const approachRegistryValue = useMemo(
    () => ({
      stableIds,
      dependencyGraph,
      setSourceOfChange,
    }),
    [stableIds, dependencyGraph, setSourceOfChange]
  );

  // Apply server-side dependency recalculations back into approach and calibration state once completed.
  // Surface the missing-approach validation only after valuation summary data has finished loading.
  useEffect(() => {
    const atLeastOneApproachRecalculated =
      !isNil(recalculatedState) && recalculatedState.some(state => state.status === 'success');
    if (atLeastOneApproachRecalculated) {
      setApproaches(prevApproaches => {
        const updatedApproaches = applyRecalculationToApproaches(prevApproaches, recalculatedState);
        valuation.valuations_approaches = updatedApproaches;
        return updatedApproaches;
      });
      setCalibrations(prevCalibrations => {
        const updatedCalibrations = applyRecalculationToCalibrations(prevCalibrations, recalculatedState);
        valuation.calibrations = updatedCalibrations;
        return updatedCalibrations;
      });
      setRecalculatedState(null);
    }
  }, [
    recalculatedState,
    applyRecalculationToApproaches,
    applyRecalculationToCalibrations,
    setApproaches,
    setCalibrations,
    valuation,
    setRecalculatedState,
  ]);

  // Currency Formats
  const [format, formatDispatch] = useFormat({ page: CAP_TABLE_CURRENCY_PAGE, sourceCurrency: companyInfo?.captable_currency });

  // Dedicated format for the Valuation Summary tab — shared by all tables on that tab.
  // FundShares strips units to '' from this format for per-share display.
  const [summaryFormat, summaryFormatDispatch] = useFormat({ page: CAP_TABLE_CURRENCY_PAGE, sourceCurrency: companyInfo?.captable_currency });

  const [approachFormats, setApproachFormats] = useState<Record<string, { format: any; dispatch: Dispatch<any> }>>({});

  const registerApproachFormat = useCallback(
    (tabId: string, approachFormat: any, approachDispatch: Dispatch<any>) => {
      setApproachFormats(prev => {
        if (prev[tabId]?.format === approachFormat) {
          return prev;
        }
        return { ...prev, [tabId]: { format: approachFormat, dispatch: approachDispatch } };
      });
    },
    []
  );

  // Register summaryFormat eagerly (not inside ValuationSummary's useEffect) so the chip
  // shows the correct format immediately when the tab becomes active, before ValuationSummary mounts.
  useEffect(() => {
    registerApproachFormat(VALUATION_SUMMARY_KEY, summaryFormat, summaryFormatDispatch);
  }, [summaryFormat, summaryFormatDispatch, registerApproachFormat]);

  const [capTableRegularFormat] = useFormat({
    page: CAP_TABLE_CURRENCY_PAGE,
    sourceCurrency: companyInfo?.captable_currency,
    units: REGULAR_UNIT,
  }) as UseFormatValues;

  const [financialRegularFormat] = useFormat({
    page: FINANCIALS_CURRENCY_PAGE,
    sourceCurrency: companyInfo?.financials_currency,
    units: REGULAR_UNIT,
  }) as UseFormatValues;

  // Refresh GPC Approaches
  const { getRefreshedApproaches: mergeStockMarketDataIntoApproaches, getTickerListsByDate } = useRefreshHelpers({
    measurementDate,
    backsolveAttributes,
    gpcAttributes: guidelinePublicCompaniesAttributes,
  });

  // Documents
  const { addReferenceForExistingDocuments } = useDocuments();

  // Notes
  const { notes, notesHasChanged, onAddNote, onDeleteNote, onUpdateNotes, saveNotes, setNotes } = useNotes();
  const {
    filesInRangeSensitivity,
    notesInRangeSensitivity,
    saveRangeSensitivityDocuments,
    saveRangeSensitivityNotes,
    saveRangeSensitivitySpreadsheetNotes,
    setFilesInRangeSensitivity,
    setNotesInRangeSensitivity,
    setSpreadsheetNotesInRangeSensitivity,
    spreadsheetNotesInRangeSensitivity,
  } = useRangeSensitivityDocumentsAndNotes({
    addReferenceForExistingDocuments,
    measurementDateId: measurementDate?.id,
    notesSaveInFlightByPanelRef,
    saveNotes,
    valuation,
  });
  const { notesInCalibration, saveCalibrationNotes, setNotesInCalibration } = useCalibrationNotes({
    calibrations: valuation.calibrations,
    notesSaveInFlightByPanelRef,
    saveNotes,
  });

  // ScalarSpreadsheet Store
  const setSynchronizedScroll = useScalarSpreadsheetStore(state => state.setSynchronizedScroll);
  const setSynchronizedSpreadsheets = useScalarSpreadsheetStore(state => state.setSynchronizedSpreadsheets);

  // Valuations Store

  // Allocation Values Store
  const setCurrentAllocationStatus = useAllocationValuesStore(state => state.setCurrentAllocationStatus);

  // Create New Version
  const { mutateAsync: createNewVersion, isPending: isCreatingNewVersion } = useCreateNewVersion();

  // Approach Query Param
  const { setApproachQueryParam } = useApproachQueryParam();

  // Tertiary nav query param state (shares `tab` param with setApproachQueryParam)
  const { getItemNavigationProps, value: activeTabValue } = useTertiaryNavigationQueryParamState({
    defaultValue: VALUATION_SUMMARY_KEY,
  });

  // Version query param for the version selector
  const { setValue: setVersionQueryParam } = useQueryParamValue({ paramName: 'version' });

  const handleVersionChange = useCallback(
    (slug: string) => {
      const callback = () => {
        if ((versions?.length ?? 0) > 1) {
          setCurrentAllocationStatus(null);
        }
        setVersionQueryParam(slug);
      };
      if (hasUnsavedChanges) {
        showUnsavedChangesGuard(callback);
      } else {
        callback();
      }
    },
    [hasUnsavedChanges, setCurrentAllocationStatus, setVersionQueryParam, showUnsavedChangesGuard, versions?.length]
  );

  // Tertiary nav item menu handler (triggered by per-tab menu button in the Prism shell)
  const handleTertiaryNavItemMenuClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>, item: TertiaryNavigationItem) => {
      setTabMenuAnchorEl(event.currentTarget);
      setMenuTabId(item.value);
    },
    []
  );

  // Save split-button dropdown handler
  const handleSaveMenuClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    setSaveMenuAnchorEl(event.currentTarget);
  }, []);

  const handleSaveMenuClose = useCallback(() => {
    setSaveMenuAnchorEl(null);
  }, []);

  // Log Invalid Cells
  const { displayInvalidCells, logInvalidCell } = useLogInvalidCells();

  // Spreadsheets
  const { areCellsValid, cells, data, evaluateSpreadsheets, onChange, workbook, workbookContextValue } = (
    useWorkbook as unknown as ValuationsUseWorkbook
  )(spreadsheets);

  const shouldSynchronizeApproaches = useMemo(
    () =>
      !areApproachesSynchronized({
        valuationApproaches,
        approaches,
      }),
    [approaches, valuationApproaches]
  );

  useInitializeApproachDependencyGraph({
    approaches,
    calibrations,
    dependencyGraph,
    stableIds,
    shouldBuild: !shouldSynchronizeApproaches,
  });

  const { shouldDisplayEquityOption } = usePCMarketDataRefresh({
    approaches,
    areSynchronized,
    companyInfo,
    compGroups,
    financialPeriods,
    getTickerListsByDate,
    mergeStockMarketDataIntoApproaches,
    isCurrentValuationDisabled,
    isLoadingPublicCompsByDate,
    marketCurrency,
    marketDate,
    publicCompsByDate,
    resetConfigurations,
    setApproaches,
    shouldSynchronizeApproaches,
    initiateStockMarketDataRefreshFlow,
    updateOPMInputs,
    valuation,
    workbook,
  });

  // Run evaluateSpreadsheets only once
  const reevaluateSpreadsheets = useMemo(
    () =>
      once(() => {
        // A sheet's columns are a snapshot taken when its configuration was built; edits made
        // since then were reverse-parsed into the model but not into that snapshot, so evaluating
        // from it would reset them (e.g. summary weights typed right after adding a scenario).
        spreadsheets.forEach(sheet => sheet.refreshColumns());
        evaluateSpreadsheets(spreadsheets).catch(handleCatchError);
      }),
    [evaluateSpreadsheets, spreadsheets]
  );

  const handleRelatedEntityAllocationScenarios = useCallback(async () => {
    const relatedEntities = getArrayValue(companyInfo?.child_companies) as RelatedEntity[];

    const parentCompanyMeasurementDate = getObjectValue(measurementDate)?.date;

    // Wait for related-entity recalculations so parent updates do not finish against stale child data.
    await Promise.all(
      relatedEntities.map(async (relatedEntity: RelatedEntity) => {
        const relatedEntityMeasurementDate = relatedEntity.company_measurement_dates.find(
          md => md.date === parentCompanyMeasurementDate
        );
        const relatedEntityAllocations = relatedEntityMeasurementDate?.allocation_ids;
        const relatedEntityValuationId = relatedEntityAllocations?.[relatedEntityAllocations.length - 1];

        if (!relatedEntityValuationId) {
          Logger.info('No valuation id found for related entity', relatedEntity);
          return;
        }

        // Check if workbook is defined before proceeding
        if (!workbook) {
          Logger.info('Workbook is not yet initialized', relatedEntity);
          return;
        }

        // get the valuation off of the equityAllocation table
        const relatedEntityEquityAllocationKey = generateEquityAllocationKey(relatedEntity?.company_id);

        // Check if the related entity equity allocation table exists in the workbook
        if (!workbook[relatedEntityEquityAllocationKey]) {
          Logger.info('Related entity equity allocation table not found in workbook', relatedEntity);
          return;
        }

        const relatedEntityValuation = workbook[relatedEntityEquityAllocationKey]?.tableData?.valuation;

        if (!relatedEntityValuation) {
          Logger.info('No valuation found for related entity', relatedEntity);
          return;
        }

        const relatedEntityAllocation = relatedEntityValuation.allocation;
        const relatedEntityAllocationScenarios = relatedEntityAllocation.allocation_scenarios;

        const relatedEntityCells = cells[relatedEntityEquityAllocationKey];

        await updateAllocationScenariosValues(
          { allocationScenarios: relatedEntityAllocationScenarios, approaches },
          { [relatedEntityEquityAllocationKey]: relatedEntityCells },
          onChange
        );
      })
    );
  }, [companyInfo, cells, onChange, approaches, measurementDate, updateAllocationScenariosValues, workbook]);

  // Await allocation-results requests so callers (e.g. cap-table change) can refresh the grid only
  // after scenario_values are written—avoids racing a second reset on stale data.
  const updateAllocationScenariosWithRelated = useCallback(
    async (scenarioParams: { allocationScenarios: any; approaches: any }, cells: any, onChange: any) => {
      await updateAllocationScenariosValues(scenarioParams, cells, onChange);

      const relatedEntities = getArrayValue(companyInfo?.child_companies);
      if (!isEmpty(relatedEntities)) {
        await handleRelatedEntityAllocationScenarios();
      }
    },
    [companyInfo, handleRelatedEntityAllocationScenarios, updateAllocationScenariosValues]
  );
  const { [`weightedShareValues_${companyInfo?.id}`]: weightedShareValuesSpreadsheets } = getObjectValue(workbook);

  useEffect(() => {
    if (cells) {
      updateAllocationScenariosWithRelated({ allocationScenarios, approaches }, cells, onChange);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cells]);

  // Re-evaluateSpreadsheets once after tabs are synchronized and cells are ready
  useEffect(() => {
    let reevaluateSpreadsheetsTimer: NodeJS.Timeout;

    if (cells && areTabsSynchronized) {
      reevaluateSpreadsheetsTimer = setTimeout(() => {
        reevaluateSpreadsheets();
      }, LOADING_TIMEOUT_DELAY);
    }

    return () => {
      if (reevaluateSpreadsheetsTimer) {
        clearTimeout(reevaluateSpreadsheetsTimer);
      }
    };
  }, [areTabsSynchronized, cells, reevaluateSpreadsheets]);

  // Page Status
  const isValidatingValuation = useMemo(() => pageStatus === VALIDATING, [pageStatus]);

  // Valuation Ownership
  const isFirmOwned = useMemo(() => isValuationFirmOwned, [isValuationFirmOwned]);
  const isScalarOwned = useMemo(() => isFalseStrict(isFirmOwned), [isFirmOwned]);
  const isReadyForAudit = useMemo(() => isValuationReadyForAudit ?? false, [isValuationReadyForAudit]);

  const isScalarOwnedDraftAndNotStaff = useMemo(
    () => isScalarOwned && !isUserStaff && !(isCurrentAllocationFinal || isCurrentAllocationPublished),
    [isCurrentAllocationFinal, isCurrentAllocationPublished, isScalarOwned, isUserStaff]
  );

  // Valuation without Approaches
  const isValuationEmpty = useMemo(() => isEmpty(valuationApproaches), [valuationApproaches]);

  // Allocation Scenarios sorted by order
  const sortedAllocationScenarios = useMemo(() => sortBy(allocationScenarios, 'order'), [allocationScenarios]);

  const hasOPMConsumer = useMemo(() => valuationUsesOPM(valuation), [valuation]);

  // Allocation without Scenarios
  const isAllocationScenariosEmpty = useMemo(() => isEmpty(sortedAllocationScenarios), [sortedAllocationScenarios]);

  const { fund_ownership: capTableFundOwnership, securities: capTableSecurities = [] } =
    getObjectValue(primaryCapTable);
  const { fund_ownership_detail: capTableFundOwnershipDetail = [] } = getObjectValue(capTableFundOwnership);

  // Fund Ownership Custom Securities
  const customSecurities = useMemo(
    () =>
      getArrayValue(capTableFundOwnershipDetail?.filter(fundOwnershipDetail => isNil(fundOwnershipDetail?.security))),
    [capTableFundOwnershipDetail]
  );

  // this will just have the ids of convertible note securities, but it will not include underlying convertible note securities
  // unless they are modeled as debt.
  const noteSecurityIdMap = useMemo(
    () =>
      capTableSecurities
        .filter(s => s.security_type === CONVERTIBLE_NOTES)
        .reduce((acc, s) => {
          if (s.id) {
            acc[s.id] = s;
          }
          return acc;
        }, {} as SecurityIdMapType),
    [capTableSecurities]
  );

  const unrealizedDebt = useMemo(
    () =>
      capTableFundOwnership?.fund_ownership_detail
        ?.filter(({ security }) => security && noteSecurityIdMap[security])
        ?.reduce((acc, { loan_value }) => Number(loan_value) + acc, 0) ?? 0,
    [capTableFundOwnership, noteSecurityIdMap]
  );

  // Fund Ownership has Custom Securities
  const hasCustomSecurities = useMemo(() => !isEmpty(customSecurities), [customSecurities]);

  // Display Allocation without Scenarios
  const shouldDisplayWithoutScenarios = useMemo(
    () => isAllocationScenariosEmpty && hasCustomSecurities,
    [isAllocationScenariosEmpty, hasCustomSecurities]
  );

  // Fund Ownership is Empty
  const isFundOwnershipEmpty = useMemo(() => isEmpty(capTableFundOwnershipDetail), [capTableFundOwnershipDetail]);

  // Cap Table with Common and Preferred Stock
  const isCapTableValid = useMemo(() => {
    const breakpointsSecurities = capTableSecurities?.filter(security =>
      [COMMON_STOCK, PREFERRED_STOCK].includes(getNumberValue(security?.security_type))
    );

    return !isEmpty(breakpointsSecurities);
  }, [capTableSecurities]);

  // Cap Table has Valid Securities or Fund Ownership has Custom Securities
  const hasSecurities = useMemo(() => isCapTableValid || hasCustomSecurities, [hasCustomSecurities, isCapTableValid]);

  // Cap Table Securities are Invalid
  const areCapTableSecuritiesInvalid = useMemo(
    () => !isCapTableValid || (!isEmpty(capTableSecurities) && !hasSecurities && !shouldDisplayWithoutScenarios),
    [capTableSecurities, hasSecurities, isCapTableValid, shouldDisplayWithoutScenarios]
  );

  // Allocation is Empty
  const isAllocationEmpty = useMemo(
    () => isAllocationScenariosEmpty && !shouldDisplayWithoutScenarios && !isFundOwnershipEmpty,
    [isAllocationScenariosEmpty, isFundOwnershipEmpty, shouldDisplayWithoutScenarios]
  );

  // Allocation is Valid
  const isAllocationValid = useMemo(
    () => (!isAllocationEmpty && !isFundOwnershipEmpty && !areCapTableSecuritiesInvalid) || unrealizedDebt > 0,
    [areCapTableSecuritiesInvalid, isAllocationEmpty, isFundOwnershipEmpty, unrealizedDebt]
  );

  // Should Display Weighted Share Values
  const shouldDisplayWeightedShareValues = useMemo(
    () => shouldDisplayWithoutScenarios || hasSecurities,
    [shouldDisplayWithoutScenarios, hasSecurities]
  );

  // The three data-ref grids below are gated by `!configurations.length` (see
  // valuationSummaryTab content). Until that array is populated the data-ref
  // element is not in the DOM, so the loading condition must include it to
  // hold the export signal until the gate opens.
  const areConfigurationsEmpty = !configurations.length;

  let valuationSummaryExportStatus: 'loading' | 'missing' | 'ready' = 'ready';
  if (isLoadingValuationSummary || isUpdatingSpreadsheets || areConfigurationsEmpty) {
    valuationSummaryExportStatus = 'loading';
  } else if (isValuationEmpty) {
    valuationSummaryExportStatus = 'missing';
  }
  useExportSignal({
    target: PdfExportTargets.VALUATION_SUMMARY.id,
    status: valuationSummaryExportStatus,
  });

  let equityAllocationExportStatus: 'loading' | 'missing' | 'ready' = 'ready';
  if (
    isLoadingValuationSummary ||
    isLoadingAllocationScenariosValues ||
    isLoadingBacksolvesEquityValues ||
    isLoadingRiskFreeRates ||
    isUpdatingSpreadsheets ||
    areConfigurationsEmpty
  ) {
    equityAllocationExportStatus = 'loading';
  } else if (isAllocationEmpty) {
    equityAllocationExportStatus = 'missing';
  }
  useExportSignal({
    target: PdfExportTargets.EQUITY_ALLOCATION.id,
    status: equityAllocationExportStatus,
  });

  let concludedShareValuesExportStatus: 'loading' | 'missing' | 'ready' = 'ready';
  if (isLoadingValuationSummary || isUpdatingSpreadsheets || areConfigurationsEmpty) {
    concludedShareValuesExportStatus = 'loading';
  } else if (!shouldDisplayWeightedShareValues) {
    concludedShareValuesExportStatus = 'missing';
  }
  useExportSignal({
    target: PdfExportTargets.CONCLUDED_SHARE_VALUES.id,
    status: concludedShareValuesExportStatus,
  });

  // The valuation-summary tab renders three independent export-capture targets
  // (summary, equity allocation, concluded share values). The export-mode
  // WidgetDrawer relay publishes the allocation notes keyed to whichever target
  // is being captured, so without this guard the notes get stamped onto all
  // three pages. Only relay them on the main valuation-summary page.
  const { shouldRelayExportWidgets } = useExportWidgetRelay(PdfExportTargets.VALUATION_SUMMARY.id, isExportMode);

  // Allocation Values - Allocation Status
  const currentAllocationStatus = useMemo(
    () => getAllocationStatus({ isFinal: isCurrentAllocationFinal, isPublished: isCurrentAllocationPublished }),
    [isCurrentAllocationFinal, isCurrentAllocationPublished]
  );

  useEffect(() => {
    if (!isExportMode) {
      return;
    }
    setCurrentAllocationStatus(ALLOCATIONS_FINAL);
    setIsCurrentAllocationFinal(true);
  }, [isExportMode, setCurrentAllocationStatus]);

  // Approaches used for Allocation Scenarios
  const approachesUsedForAllocationScenarios = useMemo(
    () =>
      allocationScenarios
        ?.filter(scenario => Boolean(scenario?.backsolve_valuation) || Boolean(scenario?.future_exit))
        ?.map(scenario => {
          const { backsolve_valuation: backsolveValuationId, future_exit: futureExitId } = getObjectValue(scenario);

          const { valuations_approach_backsolve: backsolveValuation } = getObjectValue(
            approaches?.find(approach => approach?.valuations_approach_backsolve?.id === backsolveValuationId)
          );

          const { valuations_approach_future_exit: futureExitValuation } = getObjectValue(
            approaches?.find(approach => approach?.valuations_approach_future_exit?.id === futureExitId)
          );

          return getNumberValue(backsolveValuation?.valuation_approach ?? futureExitValuation?.valuation_approach);
        })
        ?.filter(Boolean),
    [allocationScenarios, approaches]
  );

  // Number of approaches and calibrations to get the tab index
  const approachesQuantity = useMemo(
    () =>
      getNumberValue(approaches?.length) +
      getNumberValue(calibrations.length) +
      getNumberValue(valuation.range_sensitivities?.length) +
      getNumberValue(valuation.opm_calculators?.length),
    [approaches, calibrations, valuation.range_sensitivities, valuation.opm_calculators]
  );

  // Financial Versions quantity
  const financialVersionQuantity = useMemo(() => getNumberValue(financialVersions?.length), [financialVersions]);

  // Primary Financial Statement Id
  const primaryFinancialStatementId = useMemo(
    () => getNumberValue(primaryFinancialStatement?.id),
    [primaryFinancialStatement]
  );

  // Selected Financial Version
  const selectedFinancialsVersion = useMemo(() => {
    if (approachToUpdate && !isEmpty(approaches)) {
      const approachMatched = approaches?.find(approach => approach?.panelId === approachToUpdate?.panelId);

      return getNumberValue(approachMatched?.financial_statement);
    }

    return 0;
  }, [approachToUpdate, approaches]);

  // Get Allocation Values - Equity Value
  const getCurrentWeightedEquityValue = useCallback(
    (valuationSummarySpreadsheet: ConfigurationSpreadsheets) => {
      const { cells: valuationSummaryCells } = getObjectValue(valuationSummarySpreadsheet);

      // If Valuation is empty, set Weighted Equity Value to 0
      if (isValuationEmpty) {
        setCurrentWeightedEquityValue(0);
        return;
      }

      // If Summary is empty or is synchronizing, set Weighted Equity Value to null
      if (isEmpty(valuationSummarySpreadsheet) || isLoadingValuationSummary || !areSynchronized) {
        setCurrentWeightedEquityValue(null);
        return;
      }

      // If Summary Cells are empty, set Weighted Equity Value to 0
      if (isEmpty(valuationSummaryCells)) {
        setCurrentWeightedEquityValue(0);
        return;
      }

      // Evaluate Weighted Equity Value
      const totalWeightedEquityValue = getWeightedEquityValue(getObjectValue(valuationSummaryCells));

      // Set Weighted Equity Value
      setCurrentWeightedEquityValue(totalWeightedEquityValue);
    },
    [areSynchronized, isLoadingValuationSummary, isValuationEmpty]
  );

  // Get Allocation Values - Firm Total
  const getCurrentFirmTotal = useCallback(
    (wsvSpreadsheets: ConfigurationSpreadsheets[]) => {
      const weightedShareValuesCells = flatten(
        wsvSpreadsheets
          ?.map(spreadsheet => spreadsheet?.cells) // Get Cells with References
          ?.map(spreadsheetCells => Object.values(getObjectValue(spreadsheetCells))) // Get Cells without References
          ?.filter(spreadsheetCell => !isEmpty(spreadsheetCell)) // Remove empty Cells
      );

      const isWSVSheetsEmpty = isEmpty(wsvSpreadsheets);
      const isWSVCellsEmpty = isEmpty(weightedShareValuesCells);

      if (unrealizedDebt && isWSVCellsEmpty) {
        setFirmTotal(unrealizedDebt);
        return;
      }

      // If Weighted Share Values are empty, set Firm Total to 0
      if (!shouldDisplayWeightedShareValues) {
        setFirmTotal(0);
        return;
      }

      // If Weighted Share Values is empty, set Firm Total to null
      if (isWSVSheetsEmpty) {
        setFirmTotal(null);
        return;
      }
      // If Weighted Share Values Cells are empty, set Firm Total to 0
      if (isWSVCellsEmpty) {
        setFirmTotal(0);
        return;
      }

      // Evaluate Firm Total
      const currentFirmTotal =
        getFirmTotal(
          getObjectValue(weightedShareValuesCells.filter(cell => cell.isExpr)) as (ICell & {
            isRelatedEntityInvestmentInCompany: boolean;
          })[]
        ) + unrealizedDebt;

      // Set Firm Total
      setFirmTotal(currentFirmTotal);
    },
    [shouldDisplayWeightedShareValues, unrealizedDebt]
  );

  const childCompanyWSVs = useMemo(
    () =>
      companyInfo?.child_companies?.map(i => {
        const spreadsheetTableTerms = ALLOCATION_WEIGHTED_SHARE_VALUES_SPREADSHEET_TABLE_TERMS(
          `${(i as RelatedEntity).company_id}`
        );

        const childWsvTable = spreadsheets.find(sheet => sheet.name === spreadsheetTableTerms.tableName);
        return childWsvTable!;
      }) || [],
    [companyInfo, spreadsheets]
  );

  useFirmTotal({ weightedShareValuesSpreadsheets, childCompanyWSVs, getCurrentFirmTotal });

  const equityCellValues = useRef<Array<string>>([]);
  const handleEquityValueChange = useCallback(async () => {
    const { equityAllocation: equityAllocationSpreadsheet } = getObjectValue(workbook);

    if (!isEmpty(equityAllocationSpreadsheet)) {
      const currentEquityCellValues = Object.values(equityAllocationSpreadsheet.cells)
        .filter(c => c.alias === EQUITY_ALLOCATION_SPREADSHEET_PRESENT_EQUITY_VALUE && c.hidden === false)
        .map(c => c.value) as Array<string>;
      if (!currentEquityCellValues.every(v => equityCellValues.current.includes(v))) {
        equityCellValues.current = currentEquityCellValues;
        // Keep the update lock until the follow-up allocation recalculation settles.
        await updateAllocationScenariosWithRelated(
          {
            allocationScenarios,
            approaches,
          },
          cells,
          onChange
        );
      }
    }
  }, [allocationScenarios, approaches, updateAllocationScenariosWithRelated, workbook, cells, onChange]);

  // Update Allocation Values
  const updateAllocationValues = useCallback(() => {
    const { [VALUATION_SUMMARY_KEY]: valuationSummarySpreadsheet } = getObjectValue(workbook);

    if (!isEmpty(valuationSummarySpreadsheet)) {
      getCurrentWeightedEquityValue(valuationSummarySpreadsheet);
    }

    if (!isEmpty(weightedShareValuesSpreadsheets) || !isEmpty(childCompanyWSVs) || unrealizedDebt) {
      getCurrentFirmTotal([weightedShareValuesSpreadsheets, ...childCompanyWSVs]);
    }
  }, [
    childCompanyWSVs,
    getCurrentFirmTotal,
    getCurrentWeightedEquityValue,
    workbook,
    unrealizedDebt,
    weightedShareValuesSpreadsheets,
  ]);

  // Update Valuation Approaches
  const updateValuationApproaches = useCallback(
    (updatedApproaches: ValuationsApproach[]) => {
      const updatedValuation = { ...valuation, valuations_approaches: updatedApproaches } as ValuationByAllocation;

      // Set Valuation Info in Store
      dispatch?.(valuationsAction.setValuationInfo(updatedValuation));

      setValuation(updatedValuation);
    },
    [dispatch, setValuation, valuation]
  );

  // Update Allocation Scenarios
  const updateAllocationScenarios = useCallback(
    (updatedScenarios: AllocationScenarioValuationModel[]) => {
      // If there is only one scenario, set the weighting probability to 1
      const updatedScenariosWithWeightingProbability = updatedScenarios.map(scenario => ({
        ...scenario,
        weighting_probability: updatedScenarios.length === 1 ? 1 : scenario.weighting_probability,
      }));

      const updatedValuation = {
        ...valuation,
        allocation: { ...allocation, allocation_scenarios: updatedScenariosWithWeightingProbability },
      } as ValuationByAllocation;

      // Set Valuation Info in Store
      dispatch?.(valuationsAction.setValuationInfo(updatedValuation));

      setValuation(updatedValuation);
    },
    [allocation, dispatch, setValuation, valuation]
  );

  // Add Approach Dialog
  const handleOpenAddApproachDialog = useCallback(() => {
    setOpenAddApproachDialog(true);
  }, []);
  const handleCloseAddApproachDialog = useCallback(() => {
    setOpenAddApproachDialog(false);
  }, []);

  // Expand OPM Inputs on Equity Allocation
  const expandOPMInputs = useCallback(
    () =>
      setEquityAllocationRowsGroups({
        row: EQUITY_ALLOCATION_SPREADSHEET_OPM_INPUTS,
        type: SYSTEM_EXPAND,
      }),
    []
  );

  // Collapse OPM Inputs on Equity Allocation
  const collapseOPMInputs = useCallback(
    () =>
      setEquityAllocationRowsGroups({
        row: EQUITY_ALLOCATION_SPREADSHEET_OPM_INPUTS,
        type: SYSTEM_COLLAPSE,
      }),
    []
  );

  // If there is at least one OPM allocation scenario, expand the corresponding inputs
  useEffect(() => {
    if (sortedAllocationScenarios?.some(scenario => scenario.scenario_method === OPM)) {
      expandOPMInputs();
    } else {
      collapseOPMInputs();
    }
  }, [sortedAllocationScenarios, expandOPMInputs, collapseOPMInputs]);

  // Add Approach
  const handleAddApproach = useCallback(
    (approachType: ApproachTypes) => {
      const approachIdentifier = uuid();
      let newTabId = '';

      if (approachType === CALIBRATION_MODEL_NAME) {
        const defaultFinancial = financialStatementsList?.find(item => !item.is_a_version);
        const newCalibration = {
          performances: [],
          valuation: valuation.id,
          name: CALIBRATION_MODEL_NAME,
          financials_version: defaultFinancial?.id ?? 0,
          panelId: approachIdentifier,
          panel_id: approachIdentifier,
          data_updated: true,
          temp_public_comps_reference: null,
          calibration_date: valuation.market_date ?? measurementDate.date,
          public_comps_approach_calibration: null,
        } as unknown as CalibrationValuationModel;
        registerNode({
          approachId: getOrCreateId(approachIdentifier),
          panelId: buildCalibrationPanelId(approachIdentifier),
          approachType,
        });
        newTabId = valuationTabId('calibration', approachIdentifier);

        updateValuationCalibrations([...valuation.calibrations, newCalibration]);
      } else if (approachType === RANGE_SENSITIVITY_MODEL_NAME) {
        const newRangeSensitivity = {
          name: RANGE_SENSITIVITY_MODEL_NAME,
          panelId: approachIdentifier,
          panel_id: approachIdentifier,
          sensitivity_percentage: '0.1',
          valuation: getNumberValue(valuation.id),
        };
        newTabId = valuationTabId('range_sensitivity', approachIdentifier);

        updateValuationRangeSensitivities([...getArrayValue(valuation.range_sensitivities), newRangeSensitivity]);
      } else if (approachType === OPM_CALCULATOR_MODEL_NAME) {
        // Not a ValuationApproach on purpose: no `registerNode` and no approach/scenario weights,
        // which is what keeps the calculator out of valuation weighting and the summary.
        const newOPMCalculator = {
          name: OPM_CALCULATOR_MODEL_NAME,
          panelId: approachIdentifier,
          panel_id: approachIdentifier,
          valuation: getNumberValue(valuation.id),
        };
        newTabId = valuationTabId('opm_calculator', approachIdentifier);

        updateValuationOPMCalculators([...getArrayValue(valuation.opm_calculators), newOPMCalculator]);
      } else {
        const BACKSOLVE_SUBTYPE_MAP: Record<string, string> = {
          [VALUATIONS_SECONDARY_TRANSACTIONS_APPROACH]: 'secondary_transactions',
          [VALUATIONS_MUTUAL_FUND_MARKS_APPROACH]: 'mutual_fund_marks',
        };
        const backsolveSubtype = BACKSOLVE_SUBTYPE_MAP[approachType];
        const effectiveApproachType = backsolveSubtype ? VALUATIONS_BACKSOLVE_APPROACH : approachType;

        const createdApproach = {
          approach_type: effectiveApproachType,
          financial_statement: primaryFinancialStatementId,
          name: approachType,
          panel_id: approachIdentifier,
          panelId: approachIdentifier,
          valuation: getNumberValue(valuation.id),
          valuations_approaches_weights: [],
        } as NewValuationApproach;

        if (!isAllocationScenariosEmpty) {
          allocationScenarios.forEach(allocationScenario => {
            allocationScenario.allocations_scenarios_weights?.push({
              allocation_scenario_id: allocationScenario?.id ?? 0,
              approach_uuid: approachIdentifier,
              equity_value: '0',
              weight: '0',
              valuation_approach_id: 0,
            });
          });

          createdApproach.valuations_approaches_weights = getArrayValue(
            allocationScenarios?.map(allocationScenario => {
              const { id: allocationScenarioId } = getObjectValue(allocationScenario);

              return {
                allocation_scenario_id: allocationScenarioId,
                equity_value: '0',
                weight: '0',
              } as ValuationApproachWeightModel;
            })
          );
        }

        if (allocationId) {
          createdApproach.allocation = getNumberValue(allocationId);
        }

        const additionalProperties = getGenericApproachTypeObject({
          approach: createdApproach,
          capTableList: capTableVersions,
          financialStatementPeriods: financialFiscalYearPeriods,
          approaches,
        }) as ValuationApproachAdditionalProperties;

        if (backsolveSubtype) {
          (additionalProperties as any).valuations_approach_backsolve = {
            ...(additionalProperties as any).valuations_approach_backsolve,
            backsolve_type: backsolveSubtype,
            source_rows: [],
          };
        }

        const newApproach = {
          ...createdApproach,
          ...additionalProperties,
        } as ValuationsApproach;
        registerNode({
          approachId: getOrCreateId(approachIdentifier),
          panelId: approachIdentifier,
          approachType: effectiveApproachType,
        });

        updateValuationApproaches([...approaches, newApproach]);
        newTabId = valuationTabId('approach', getApproachTableName({ approach: newApproach }));
      }

      setPageStatus(VALIDATED);
      setArePendingChanges(true);
      setApproachQueryParam(newTabId);
      handleCloseAddApproachDialog();

      return approachIdentifier;
    },
    [
      allocationId,
      allocationScenarios,
      approaches,
      capTableVersions,
      financialFiscalYearPeriods,
      financialStatementsList,
      handleCloseAddApproachDialog,
      isAllocationScenariosEmpty,
      primaryFinancialStatementId,
      updateValuationApproaches,
      updateValuationCalibrations,
      valuation.calibrations,
      valuation.id,
      valuation.market_date,
      measurementDate.date,
      registerNode,
      getOrCreateId,
      setApproachQueryParam,
      updateValuationRangeSensitivities,
      valuation.range_sensitivities,
      updateValuationOPMCalculators,
      valuation.opm_calculators,
    ]
  );

  // Auto-create approach when the parent passes an autoCreateApproach value (e.g. navigating
  // from the company overview ZX card with { autoCreateApproach: 'Secondary Transactions' }).
  const autoCreateProcessedRef = useRef(false);
  useEffect(() => {
    if (autoCreateProcessedRef.current || !autoCreateApproach) return;
    // Wait until local approaches mirror valuation.valuations_approaches
    if (shouldSynchronizeApproaches) return;

    autoCreateProcessedRef.current = true;
    handleAddApproach(autoCreateApproach as ApproachTypes);
  }, [autoCreateApproach, handleAddApproach, shouldSynchronizeApproaches]);

  // Add Scenario
  const handleAddScenario = useCallback(() => {
    const scenarioIdentifier = uuid();

    const createdScenariosWeights = approaches?.map(approach => {
      const { equity_value: approachEquityValue, id: approachId, panel_id: approachPanelId } = getObjectValue(approach);

      return {
        allocation_scenario_id: 0,
        approach_uuid: getStringValue(approachPanelId),
        equity_value: getStringValue(approachEquityValue ?? '0'),
        valuation_approach_id: getNumberValue(approachId),
        weight: '0',
      };
    });

    const createdScenario = {
      allocation: getNumberValue(allocationId),
      allocations_scenarios_weights: createdScenariosWeights,
      asRef: scenarioIdentifier,
      cap_table_id: getNumberValue(primaryCapTable?.id),
      cap_table: primaryCapTable,
      order: getNumberValue(maxBy(sortedAllocationScenarios, 'order')?.order) + 1,
      scenario_ref: scenarioIdentifier,
      weighting_probability: (allocationScenarios ?? []).length === 0 ? '1' : '0',
      volatility_source:
        getDefaultCompsApproachForDropdown(approaches, [VALUATIONS_PUBLIC_COMPANIES_APPROACH])?.valuations_approach_gpc
          ?.id ?? null,
      volatility:
        getDefaultCompsApproachForDropdown(approaches, [VALUATIONS_PUBLIC_COMPANIES_APPROACH])?.valuations_approach_gpc
          ?.one_year_volatility ?? null,
    } as NewAllocationScenario;

    const newScenario = {
      ...ALLOCATION_SCENARIO_DEFAULT_VALUES,
      ...createdScenario,
    } as NewAllocationScenario;

    updateAllocationScenarios([...getArrayValue(allocationScenarios), newScenario]);

    setPageStatus(VALIDATED);
    setArePendingChanges(true);
    handleCloseAddApproachDialog();

    setTimeout(() => {
      setApproachQueryParam(VALUATION_SUMMARY_KEY);
      setSynchronizedScroll({
        synchronizedBy: null,
        synchronizedScrollLeft: Number.MAX_SAFE_INTEGER,
      });
    }, REDIRECT_TIMEOUT_DELAY);
  }, [
    allocationId,
    allocationScenarios,
    approaches,
    handleCloseAddApproachDialog,
    primaryCapTable,
    setApproachQueryParam,
    setSynchronizedScroll,
    sortedAllocationScenarios,
    updateAllocationScenarios,
  ]);

  const companyPageActionHandlers = useMemo<CompanyPageActionHandlers>(() => {
    const getApproachHandler = (approachType: ApproachTypes, isAvailable = true): CompanyPageActionHandler => {
      if (isLoadingValuationSummary) {
        return undefined;
      }

      return !isCurrentValuationDisabled && isAvailable ? () => handleAddApproach(approachType) : false;
    };
    const hasPublicCompsApproach = approaches.some(
      approach => approach?.approach_type === VALUATIONS_PUBLIC_COMPANIES_APPROACH
    );
    const canAddScenario =
      !isCurrentValuationDisabled &&
      !areCapTableSecuritiesInvalid &&
      !isFundOwnershipEmpty &&
      approaches.length > 0;
    let addScenarioHandler: CompanyPageActionHandler;

    if (isLoadingValuationSummary) {
      addScenarioHandler = undefined;
    } else {
      addScenarioHandler = canAddScenario ? handleAddScenario : false;
    }

    return {
      [COMPANY_PAGE_ACTIONS.ADD_ALLOCATION_SCENARIO]: addScenarioHandler,
      [COMPANY_PAGE_ACTIONS.ADD_BACKSOLVE_APPROACH]: getApproachHandler(VALUATIONS_BACKSOLVE_APPROACH),
      [COMPANY_PAGE_ACTIONS.ADD_CALIBRATION]: getApproachHandler(CALIBRATION_MODEL_NAME, hasPublicCompsApproach),
      [COMPANY_PAGE_ACTIONS.ADD_DCF_APPROACH]: getApproachHandler(VALUATIONS_DISCOUNT_CASH_FLOW_APPROACH),
      [COMPANY_PAGE_ACTIONS.ADD_EXTERNAL_VALUATION_APPROACH]: getApproachHandler(
        VALUATIONS_EXTERNAL_VALUATION_APPROACH
      ),
      [COMPANY_PAGE_ACTIONS.ADD_FUTURE_EXIT_APPROACH]: getApproachHandler(VALUATIONS_FUTURE_EXIT_APPROACH),
      [COMPANY_PAGE_ACTIONS.ADD_PUBLIC_COMPS_APPROACH]: getApproachHandler(
        VALUATIONS_PUBLIC_COMPANIES_APPROACH
      ),
      [COMPANY_PAGE_ACTIONS.ADD_RANGE_SENSITIVITY]: getApproachHandler(RANGE_SENSITIVITY_MODEL_NAME),
      [COMPANY_PAGE_ACTIONS.ADD_SECONDARY_TRANSACTIONS_APPROACH]: getApproachHandler(
        VALUATIONS_SECONDARY_TRANSACTIONS_APPROACH
      ),
      [COMPANY_PAGE_ACTIONS.ADD_SPECIFIED_SHARE_VALUES_APPROACH]: getApproachHandler(
        VALUATIONS_SPECIFIED_SHARE_VALUES_APPROACH
      ),
      [COMPANY_PAGE_ACTIONS.ADD_TRANSACTION_COMPS_APPROACH]: getApproachHandler(
        VALUATIONS_PUBLIC_TRANSACTIONS_APPROACH
      ),
    };
  }, [
    approaches,
    areCapTableSecuritiesInvalid,
    handleAddApproach,
    handleAddScenario,
    isCurrentValuationDisabled,
    isFundOwnershipEmpty,
    isLoadingValuationSummary,
  ]);

  useCompanyPageAction({ handlers: companyPageActionHandlers });

  // Delete Scenario
  const handleDeleteScenario = useCallback(
    (columnIndex: number) => {
      const deletedScenarioIndex = columnIndex - (FIRST_ALLOCATION_SCENARIOS_COLUMN_NUMBER - 1);

      const scenarioToBeDeleted = sortedAllocationScenarios?.[deletedScenarioIndex];

      const { id: deletedScenarioId } = getObjectValue(scenarioToBeDeleted);

      setDeletedScenariosIds(previousState => [
        ...previousState,
        ...(!isNil(deletedScenarioId) ? [deletedScenarioId] : []),
      ]);

      const updatedScenarios = allocationScenarios?.filter(scenario => scenario?.id !== deletedScenarioId);

      updateAllocationScenarios(updatedScenarios);

      setPageStatus(VALIDATED);
      setArePendingChanges(true);
    },
    [allocationScenarios, sortedAllocationScenarios, updateAllocationScenarios]
  );

  // Update Weighted Share Values
  const handleWeightedShareValues = useCallback(() => {
    const weightedShareValuesSpreadsheets = spreadsheets?.filter(
      spreadsheet => spreadsheet?.page === ALLOCATION_WEIGHTED_SHARE_VALUES_KEY
    );

    setTimeout(() => {
      weightedShareValuesSpreadsheets.forEach(spreadsheet => {
        spreadsheet.reverseParse();
      });
    }, LOADING_TIMEOUT_DELAY);
  }, [spreadsheets]);

  // Update Valuation Summary and Equity Allocation Spreadsheets
  const updateMainSpreadsheets = useCallback(
    (params?: UpdateMainSpreadsheetsParams) => {
      const { updatedSecurities } = getObjectValue(params);

      const summarySpreadsheet = spreadsheets?.find(spreadsheet => spreadsheet?.name === VALUATION_SUMMARY_KEY);
      const allocationSpreadsheet = spreadsheets?.find(spreadsheet => spreadsheet?.name === EQUITY_ALLOCATION_KEY);

      if (summarySpreadsheet && allocationSpreadsheet) {
        // Valuation Summary
        const updatedSummaryColumns = createValuationSummaryColumns({
          allocationScenarios: sortedAllocationScenarios,
          configurations,
          deletedApproachesIds,
          isUniformCurrency,
        });

        summarySpreadsheet.reset({
          columns: updatedSummaryColumns as ResetParams<ValuationSummaryColumn>['columns'],
        });

        // Equity Allocation
        const updatedAllocationColumns = createEquityAllocationColumns({
          allocationScenarios: sortedAllocationScenarios,
          approaches,
          isUniformCurrency,
          riskFreeRates,
          securities: updatedSecurities ?? securities,
        });

        const updatedAllocationRows = createEquityAllocationRows({
          isDisabled: isCurrentValuationDisabled,
          securities: updatedSecurities ?? securities,
          shouldBeHidden: (sortedAllocationScenarios ?? []).length <= 1,
          // reset() replaces rowConfig; without GPC options the volatility-source select is empty.
          volatilitySourceOptions: getEquityAllocationVolatilitySourceOptions(approaches),
        });

        allocationSpreadsheet.reset({
          columns: updatedAllocationColumns as ResetParams<EquityAllocationColumn>['columns'],
          rowConfig: updatedAllocationRows as ResetParams<EquityAllocationColumn>['rowConfig'],
          // Supply fresh securities so the parser can use them instead of its stale closure value.
          // Without this, switching cap table versions leaves allSecuritiesExpression and
          // securityNameSharesMap pointing at the old security list, zeroing computed totals.
          tableData: { ...allocationSpreadsheet.tableData, securities: updatedSecurities ?? securities },
        });

        // Update Weighted Share Values
        handleWeightedShareValues();

        // Reset Configurations
        resetConfigurations();
      }
    },
    [
      approaches,
      configurations,
      deletedApproachesIds,
      handleWeightedShareValues,
      isCurrentValuationDisabled,
      isUniformCurrency,
      resetConfigurations,
      riskFreeRates,
      securities,
      sortedAllocationScenarios,
      spreadsheets,
    ]
  );

  // Workbook
  const { onWorkbookChange, handleOnSpreadsheetChange } = useEquityAllocationInputHandlers({
    allocationScenarios,
    approaches,
    cells,
    onChange,
    updateAllocationScenariosWithRelated,
    updateMainSpreadsheets,
    updateOPMInputs,
    updateSecurities,
    updateAllocationValues,
    handleAddApproach,
    expandOPMInputs,
    collapseOPMInputs,
    setIsUpdatingSpreadsheets,
    handleEquityValueChange,
    setChangedCells,
  });

  const RelatedEntityEquityAllocationTables = useMemo(
    () =>
      configurations
        .filter((config: Configuration) => config.relatedEntity && config.isEquityAllocation)
        .map(config => (
          <CustomContainer key={config.relatedEntity?.company_id}>
            <EquityAllocation
              relatedEntity={config.relatedEntity}
              areCapTableSecuritiesInvalid={areCapTableSecuritiesInvalid}
              collapsibleColumns={collapsibleColumns}
              equityAllocationRowsGroups={equityAllocationRowsGroups}
              format={summaryFormat}
              formatDispatch={summaryFormatDispatch}
              handleDeleteScenario={handleDeleteScenario}
              isAllocationEmpty={isAllocationEmpty}
              isFundOwnershipEmpty={isFundOwnershipEmpty}
              isLoading={
                isLoadingAllocationScenariosValues ||
                isLoadingBacksolvesEquityValues ||
                isLoadingRiskFreeRates ||
                isUpdatingSpreadsheets
              }
              isLoadingValuationSummary={isLoadingValuationSummary}
              isSaving={isUpdatingValuation}
              measurementDate={measurementDate}
              onSpreadsheetChange={handleOnSpreadsheetChange}
              setCollapsibleColumns={setCollapsibleColumns}
              setEquityAllocationRowsGroups={setEquityAllocationRowsGroups}
              workbook={workbook}
            />

            <WeightedShareValues
              desiredWSVTableName={config.relatedEntity?.company_id?.toString()}
              format={summaryFormat}
              formatDispatch={summaryFormatDispatch}
              isLoading={isUpdatingSpreadsheets}
              isLoadingValuationSummary={isLoadingValuationSummary}
              isSaving={isUpdatingValuation}
              onSpreadsheetChange={handleOnSpreadsheetChange}
              shouldDisplayWeightedShareValues={shouldDisplayWeightedShareValues}
              weightedShareValuesNames={weightedShareValuesNames}
              workbook={workbook}
            />
          </CustomContainer>
        )),
    [
      weightedShareValuesNames,
      shouldDisplayWeightedShareValues,
      configurations,
      areCapTableSecuritiesInvalid,
      collapsibleColumns,
      equityAllocationRowsGroups,
      handleDeleteScenario,
      handleOnSpreadsheetChange,
      isAllocationEmpty,
      isFundOwnershipEmpty,
      isLoadingAllocationScenariosValues,
      isLoadingBacksolvesEquityValues,
      isLoadingRiskFreeRates,
      isLoadingValuationSummary,
      isUpdatingSpreadsheets,
      isUpdatingValuation,
      measurementDate,
      setCollapsibleColumns,
      setEquityAllocationRowsGroups,
      summaryFormat,
      summaryFormatDispatch,
      workbook,
    ]
  );

  // Valuation Summary tab
  const valuationSummaryTab = useMemo(
    () =>
      ({
        id: VALUATION_SUMMARY_KEY,
        pdfExportId: toPdfExportTabId('valuation_summary'),
        label: VALUATION_SUMMARY_LABEL,
        content: !configurations.length ? (
          <></>
        ) : (
          <CustomContainer>
            <Grid
              data-ref={PdfExportTargets.VALUATION_SUMMARY.id}
              size={{ xs: 12 }}
              style={isExportMode ? EXPORT_MODE_STYLES : {}}>
              <ValuationSummary
                collapsibleColumns={collapsibleColumns}
                format={summaryFormat}
                formatDispatch={summaryFormatDispatch}
                isLoading={isUpdatingSpreadsheets}
                isLoadingValuationSummary={isLoadingValuationSummary}
                isSaving={isUpdatingValuation}
                isValuationEmpty={isValuationEmpty}
                onSpreadsheetChange={handleOnSpreadsheetChange}
                setCollapsibleColumns={setCollapsibleColumns}
                setIsLoadingValuationSummary={setIsLoadingValuationSummary}
                workbook={workbook}
              />
            </Grid>

            <Grid
              data-ref={PdfExportTargets.EQUITY_ALLOCATION.id}
              size={{ xs: 12 }}
              style={isExportMode ? EXPORT_MODE_STYLES : {}}>
              <EquityAllocation
                areCapTableSecuritiesInvalid={areCapTableSecuritiesInvalid}
                collapsibleColumns={collapsibleColumns}
                equityAllocationRowsGroups={equityAllocationRowsGroups}
                format={summaryFormat}
                formatDispatch={summaryFormatDispatch}
                handleDeleteScenario={handleDeleteScenario}
                isAllocationEmpty={isAllocationEmpty}
                isFundOwnershipEmpty={isFundOwnershipEmpty}
                isLoading={
                  isLoadingAllocationScenariosValues ||
                  isLoadingBacksolvesEquityValues ||
                  isLoadingRiskFreeRates ||
                  isUpdatingSpreadsheets
                }
                isLoadingValuationSummary={isLoadingValuationSummary}
                isSaving={isUpdatingValuation}
                measurementDate={measurementDate}
                onSpreadsheetChange={handleOnSpreadsheetChange}
                setCollapsibleColumns={setCollapsibleColumns}
                setEquityAllocationRowsGroups={setEquityAllocationRowsGroups}
                workbook={workbook}
              />
            </Grid>

            <Grid
              data-ref={PdfExportTargets.CONCLUDED_SHARE_VALUES.id}
              size={{ xs: 12 }}
              style={isExportMode ? EXPORT_MODE_STYLES : {}}>
              <WeightedShareValues
                format={summaryFormat}
                formatDispatch={summaryFormatDispatch}
                isLoading={isUpdatingSpreadsheets}
                isLoadingValuationSummary={isLoadingValuationSummary}
                isSaving={isUpdatingValuation}
                onSpreadsheetChange={handleOnSpreadsheetChange}
                shouldDisplayWeightedShareValues={shouldDisplayWeightedShareValues}
                weightedShareValuesNames={weightedShareValuesNames}
                workbook={workbook}
                desiredWSVTableName={companyInfo?.id?.toString()}
              />
            </Grid>
            {RelatedEntityEquityAllocationTables}

            {/* Allocation Notes  */}
            {shouldRelayExportWidgets && (
              <WidgetDrawer
                applicationContext={WidgetDrawerApplicationContext.VALUATION_APPROACHES}
                notesProps={{
                  isDisabled: isCurrentValuationDisabled,
                  notes,
                  onAddNote,
                  onDeleteNote,
                  onUpdateNotes,
                  pageType: ALLOCATIONS_REFERENCE_TYPE,
                  pageTypeId: allocationId,
                  pageTypeKey: ALLOCATIONS_PAGE_KEY,
                  setNotes,
                }}
              />
            )}
          </CustomContainer>
        ),
      }),
    [
      RelatedEntityEquityAllocationTables,
      allocationId,
      areCapTableSecuritiesInvalid,
      collapsibleColumns,
      companyInfo?.id,
      configurations.length,
      equityAllocationRowsGroups,
      handleDeleteScenario,
      handleOnSpreadsheetChange,
      isAllocationEmpty,
      isCurrentValuationDisabled,
      isFundOwnershipEmpty,
      isLoadingAllocationScenariosValues,
      isLoadingBacksolvesEquityValues,
      isLoadingRiskFreeRates,
      isLoadingValuationSummary,
      isExportMode,
      isUpdatingSpreadsheets,
      isUpdatingValuation,
      isValuationEmpty,
      measurementDate,
      notes,
      onAddNote,
      onDeleteNote,
      onUpdateNotes,
      setNotes,
      shouldDisplayWeightedShareValues,
      shouldRelayExportWidgets,
      summaryFormat,
      summaryFormatDispatch,
      weightedShareValuesNames,
      workbook,
    ]
  );

  const valuationsAllocationTablesNames = useMemo(
    () =>
      getTableNames({
        approaches,
        rangeSensitivities: valuation?.range_sensitivities,
        weightedShareValuesNames,
      }),
    [approaches, valuation?.range_sensitivities, weightedShareValuesNames]
  );

  const handleMenuOpen = (event: MouseEvent<HTMLSpanElement>) => setMenuAnchorEl(event.currentTarget);

  // New Version Dialog
  const handleOpenNewVersionDialog = useCallback(() => setOpenNewVersionDialog(true), []);

  const handleCloseNewVersionDialog = useCallback(() => {
    setOpenNewVersionDialog(false);
  }, []);

  // Update Approach Dialog
  const handleCloseUpdateApproachDialog = () => setOpenUpdateApproachDialog(false);

  // Update Guideline Public Companies Approach
  const updateGPCApproach = useCallback(params => {
    const {
      updatedApproach: approach,
      isEvaluatingEquityValue,
      useMultiplePremiumDiscount,
      useForwardMultipleDiscount,
    } = params;

    const updatedApproach = { ...approach };

    if (updatedApproach.approach_type === VALUATIONS_PUBLIC_COMPANIES_APPROACH) {
      updatedApproach.valuations_approach_gpc.is_evaluating_equity_value = isEvaluatingEquityValue;
      updatedApproach.valuations_approach_gpc.use_multiple_premium_discount = useMultiplePremiumDiscount;
      updatedApproach.valuations_approach_gpc.use_forward_multiple_discount = useForwardMultipleDiscount;
    }

    return updatedApproach;
  }, []);

  // Update DCF Approach
  const updateDCFApproach = useCallback(
    params => {
      const { approachOutdated, updatedApproach: approach, updatedFinancialStatementId } = params;

      let updatedApproach = { ...approach };

      const currentApproach = currentValuationApproaches.current?.find(
        valuationApproach =>
          (!isNil(approach?.id) && valuationApproach?.id === updatedApproach?.id) ||
          (!isNil(updatedApproach?.panelId) && valuationApproach?.panelId === updatedApproach?.panelId) ||
          (!isNil(updatedApproach?.panel_id) && valuationApproach?.panel_id === updatedApproach?.panel_id)
      );

      if (
        updatedApproach.approach_type === VALUATIONS_DISCOUNT_CASH_FLOW_APPROACH &&
        !isNull(updatedFinancialStatementId) &&
        approachOutdated?.financial_statement !== updatedFinancialStatementId
      ) {
        updatedApproach = updateDCFPeriodsByVersion({
          currentApproach,
          otherFinancialStatements,
          primaryFinancialStatement,
          updatedApproach,
        });
      }

      return updatedApproach;
    },
    [otherFinancialStatements, primaryFinancialStatement, currentValuationApproaches]
  );

  // Update Approach from Dialog
  const handleUpdateApproach = useCallback(
    async (formState: ValuationsFormState) => {
      const { values } = getObjectValue(formState);

      if (!isEmpty(approachToUpdate) && !isEmpty(values)) {
        const {
          name: updatedName,
          enterprise_equity_value: enterpriseEquityValue,
          is_calibration: isCalibration,
          use_multiple_premium_discount: useMultiplePremiumDiscount,
          use_forward_multiple_discount: useForwardMultipleDiscount,
          financial_statement_id: currentFinancialStatementId,
        } = getObjectValue(values);

        const approachToUpdateIndex = approaches?.findIndex(
          approach => approach?.panelId === approachToUpdate?.panelId
        );
        const approachOutdated = approaches[approachToUpdateIndex];

        const firstFinancialStatement = financialVersions?.[0];

        const updatedFinancialStatementId = getNumberValue(currentFinancialStatementId ?? firstFinancialStatement?.id);

        let updatedApproach = {
          ...approachOutdated,
          name: getStringValue(updatedName ?? approachOutdated.name),
        } as ValuationApproachWithReferences;

        // Update Financial Statement Version
        if (!isNil(updatedFinancialStatementId)) {
          updatedApproach.financial_statement = updatedFinancialStatementId;
        }
        const isEvaluatingEquityValue = enterpriseEquityValue === EQUITY_VALUE_OPTION;
        // Guideline Public Companies
        updatedApproach = updateGPCApproach({
          approachOutdated,
          updatedApproach,
          isEvaluatingEquityValue,
          useMultiplePremiumDiscount,
          useForwardMultipleDiscount,
        });

        // Discounted Cash Flow
        updatedApproach = updateDCFApproach({ approachOutdated, updatedApproach, updatedFinancialStatementId });

        updatedApproach.is_calibration = getBooleanValue(isCalibration);

        const updatedApproaches = approaches?.filter(
          valuationApproach => valuationApproach?.panelId !== approachToUpdate?.panelId
        );

        updatedApproaches?.splice(approachToUpdateIndex, 0, updatedApproach);

        updateValuationApproaches(updatedApproaches);
        setPageStatus(VALIDATED);
        setArePendingChanges(true);
      }

      if (!isEmpty(calibrationToUpdate)) {
        const { name: updatedName } = getObjectValue(values);

        const calibrationToUpdateIndex = valuation.calibrations?.findIndex(
          calibration => calibration.panelId === calibrationToUpdate?.panelId
        );

        const updatedCalibrations = [...valuation.calibrations];

        updatedCalibrations.splice(calibrationToUpdateIndex, 1, {
          ...updatedCalibrations[calibrationToUpdateIndex],
          name: updatedName ?? '',
        });

        updateValuationCalibrations(updatedCalibrations);

        setPageStatus(VALIDATED);
        setArePendingChanges(true);
      }

      if (!isEmpty(rangeSensitivityToUpdate) && !isEmpty(values)) {
        const { name: updatedName } = getObjectValue(values);
        const targetPanelId = getStringValue(
          (rangeSensitivityToUpdate as { panelId?: string }).panelId ?? rangeSensitivityToUpdate.panel_id
        );

        const rangeSensitivityToUpdateIndex = valuation.range_sensitivities?.findIndex(rs => {
          const id = getStringValue((rs as { panelId?: string }).panelId ?? rs.panel_id);
          return id === targetPanelId;
        });

        if (rangeSensitivityToUpdateIndex != null && rangeSensitivityToUpdateIndex >= 0) {
          const updatedRangeSensitivities = [...getArrayValue(valuation.range_sensitivities)];

          updatedRangeSensitivities.splice(rangeSensitivityToUpdateIndex, 1, {
            ...updatedRangeSensitivities[rangeSensitivityToUpdateIndex],
            name: updatedName ?? '',
          });

          updateValuationRangeSensitivities(updatedRangeSensitivities);

          setPageStatus(VALIDATED);
          setArePendingChanges(true);
        }
      }

      if (isEditingOPMCalculatorName && !isEmpty(opmCalculatorToUpdate) && !isEmpty(values)) {
        const { name: updatedName } = getObjectValue(values);

        handleUpdateOPMCalculatorName(
          getOPMCalculatorPanelId(opmCalculatorToUpdate),
          getStringValue(updatedName)
        );
      }
    },
    [
      approachToUpdate,
      approaches,
      calibrationToUpdate,
      rangeSensitivityToUpdate,
      opmCalculatorToUpdate,
      isEditingOPMCalculatorName,
      handleUpdateOPMCalculatorName,
      financialVersions,
      updateDCFApproach,
      updateGPCApproach,
      updateValuationApproaches,
      updateValuationCalibrations,
      updateValuationRangeSensitivities,
      valuation,
    ]
  );

  // Update Approach Dialog
  const handleUpdateApproachDialog = useCallback(
    (approachPanelId: string) => {
      if (approachPanelId) {
        handleMenuClose();

        const approachToBeUpdated = approaches?.find(
          valuationApproach => valuationApproach?.panelId === approachPanelId
        );

        if (approachToBeUpdated) {
          setCalibrationToUpdate(null);
          setRangeSensitivityToUpdate(null);
          setOPMCalculatorToUpdate(null);
          handleOpenUpdateApproachDialog();
          setApproachToUpdate(approachToBeUpdated);
        }
      }
    },
    [
      approaches,
      handleMenuClose,
      handleOpenUpdateApproachDialog,
      setCalibrationToUpdate,
      setRangeSensitivityToUpdate,
      setOPMCalculatorToUpdate,
    ]
  );

  // Update Approach Name Dialog
  const showUpdateApproachNameDialog = useCallback(
    (approachPanelId: string) => {
      if (approachPanelId) {
        setIsEditingCalibrationName(false);
        setIsEditingRangeSensitivityName(false);
        setIsEditingApproachName(true);
        setIsChangingFinancialStatement(false);
        handleUpdateApproachDialog(approachPanelId);
      }
    },
    [handleUpdateApproachDialog]
  );

  // Delete Approach
  const handleDeleteApproach = useCallback(
    (approachPanelId: string) => {
      if (approachPanelId) {
        const approachToBeDeleted = approaches?.find(
          valuationApproach => valuationApproach?.panelId === approachPanelId
        );
        const { id: deletedApproachId } = getObjectValue(approachToBeDeleted);

        setDeletedApproachesIds(previousState => [
          ...previousState,
          ...(!isNil(deletedApproachId) ? [deletedApproachId] : []),
        ]);

        const updatedApproaches = approaches?.filter(
          valuationApproach => valuationApproach?.panelId !== approachPanelId
        );

        updateValuationApproaches(updatedApproaches);
        setPageStatus(VALIDATED);
        setArePendingChanges(true);

        setTimeout(() => {
          setApproachQueryParam(VALUATION_SUMMARY_KEY);
        }, REDIRECT_TIMEOUT_DELAY);
      }
    },
    [approaches, setApproachQueryParam, updateValuationApproaches]
  );

  // Delete Approach Dialog
  const showDeleteApproachDialog = useCallback(
    (approachPanelId: string) => {
      if (approachPanelId) {
        handleMenuClose();
        const message =
          'This approach cannot be deleted because it is being used in at least one guideline calibration. Remove it or select a different approach';
        const contentCalibrationMessage = () => (
          <DeleteApproachDialog customMessage={message} isReferencedApproach={false} />
        );

        let relatedCalibration = false;
        if (valuation.calibrations.length > 0) {
          const { calibrations } = valuation;
          calibrations.forEach(calibration => {
            if (calibration.public_comps_approach_calibration?.toString() === approachPanelId) {
              relatedCalibration = true;
            }
          });
        }

        if (relatedCalibration) {
          showDialog({
            wrapperProps: {
              title: 'Action not allowed',
            },
            content: contentCalibrationMessage,
            wrapper: ConfirmationDialog,
            actions: [
              {
                label: 'Cancel',
                variant: 'contained',
                type: 'primary',
              },
            ],
          });
        } else {
          const approachToBeDeleted = approaches?.find(
            valuationApproach => valuationApproach?.panelId === approachPanelId
          );

          const isReferencedApproach = approachesUsedForAllocationScenarios?.includes(
            getNumberValue(approachToBeDeleted?.id)
          );

          const content = () => <DeleteApproachDialog isReferencedApproach={isReferencedApproach} />;

          showDialog({
            actions: [
              {
                callback: () => handleDeleteApproach(approachPanelId),
                label: DELETE_VALUATION_BUTTON_LABEL,
                type: 'danger',
                variant: 'contained',
              },
            ],
            content,
            wrapper: ConfirmationDialog,
          });
        }
      }
    },
    [approaches, approachesUsedForAllocationScenarios, handleMenuClose, handleDeleteApproach, showDialog, valuation]
  );

  // Delete Approach Dialog
  const showDeleteCalibrationDialog = useCallback(
    (panelIdParam: string) => {
      if (panelIdParam) {
        handleMenuClose();
        const content = () => <DeleteCalibrationDialog />;

        showDialog({
          actions: [
            {
              callback: () => handleDeleteCalibration(panelIdParam),
              label: DELETE_VALUATION_BUTTON_LABEL,
              type: 'danger',
              variant: 'contained',
            },
          ],
          content,
          wrapper: ConfirmationDialog,
        });
      }
    },
    [handleDeleteCalibration, handleMenuClose, showDialog]
  );

  const showDeleteRangeSensitivityDialog = useCallback(
    (panelIdParam: string) => {
      if (panelIdParam) {
        handleMenuClose();
        const content = () => <DeleteRangeSensitivityDialog />;

        showDialog({
          actions: [
            {
              callback: () => handleDeleteRangeSensitivity(panelIdParam),
              label: DELETE_VALUATION_BUTTON_LABEL,
              type: 'danger',
              variant: 'contained',
            },
          ],
          content,
          wrapper: ConfirmationDialog,
        });
      }
    },
    [handleDeleteRangeSensitivity, showDialog, handleMenuClose]
  );

  const showDeleteOPMCalculatorDialog = useCallback(
    (panelIdParam: string) => {
      if (panelIdParam) {
        handleMenuClose();
        const content = () => <DeleteOPMCalculatorDialog />;

        showDialog({
          actions: [
            {
              callback: () => handleDeleteOPMCalculator(panelIdParam),
              label: DELETE_VALUATION_BUTTON_LABEL,
              type: 'danger',
              variant: 'contained',
            },
          ],
          content,
          wrapper: ConfirmationDialog,
        });
      }
    },
    [handleDeleteOPMCalculator, showDialog, handleMenuClose]
  );

  const isUpdatedApproachTypeOf = useCallback(
    (approachType: ApproachTypes) => {
      if (approachToUpdate) {
        const approachToBeUpdated = approaches?.find(approach => approach?.panelId === approachToUpdate?.panelId);
        return approachToBeUpdated?.approach_type === approachType;
      }

      return false;
    },
    [approachToUpdate, approaches]
  );

  // Update Financial Statement Dialog
  const showUpdateFinancialStatementDialog = useCallback(
    (approachPanelId: string) => {
      if (approachPanelId) {
        setIsChangingFinancialStatement(true);
        setIsEditingApproachName(false);
        handleUpdateApproachDialog(approachPanelId);
      }
    },
    [handleUpdateApproachDialog]
  );

  // Save Documents
  const saveApproachDocuments = useCallback(
    async (approach: ValuationsApproach) => {
      if (!isEmpty(filesInApproach)) {
        // Save Files for each Approach
        const filesToBeSaved = filesInApproach?.filter(file => file?.panelId === approach?.panelId);

        // Add Reference to Files to be saved
        const filesReferences = filesToBeSaved?.map(async file =>
          addReferenceForExistingDocuments(
            getNumberValue(measurementDate?.id),
            getArrayValue(file?.filesToSave),
            getNumberValue(approach?.id),
            VALUATIONS_VALUATION_REFERENCES_MAP.get(getStringValue(approach?.approach_type))
          )
        );

        await Promise.all(filesReferences);

        setFilesInApproach([]);
      }
    },
    [addReferenceForExistingDocuments, filesInApproach, measurementDate]
  );

  // Save Approaches Notes
  const saveApproachNotes = useCallback(
    (approach: ValuationsApproach) => {
      const notesToBeSaved = notesInApproach?.filter(note => note?.panelId === approach?.panelId) ?? [];

      savePanelNotesEntries({
        notes: notesToBeSaved,
        notesSaveInFlightByPanelRef,
        saveNotes,
        setNotes: setNotesInApproach,
      });
    },
    [notesInApproach, saveNotes]
  );

  // Save Allocation Notes
  const saveAllocationNotes = useCallback(() => {
    if (!isEmpty(notes) && notesHasChanged) {
      saveNotes(getArrayValue(notes), {
        onSuccess: savedNotes => {
          setNotes(savedNotes);
        },
      });
    }
  }, [notes, notesHasChanged, saveNotes, setNotes]);

  // Save/Update Documents and Notes
  const saveValuationAllocationDocumentsAndNotes = useCallback(() => {
    // Save Documents and Notes for each Approach
    if (!isEmpty(approaches) && (!isEmpty(filesInApproach) || !isEmpty(notesInApproach))) {
      approaches?.forEach(approach => {
        if (!isNil(approach?.id)) {
          // Documents
          saveApproachDocuments(approach);

          // Notes
          saveApproachNotes(approach);
        }
      });
    }

    // Save Range Sensitivity Notes
    saveRangeSensitivityNotes();

    // Save Range Sensitivity Documents
    saveRangeSensitivityDocuments();

    // Save Range Sensitivity Spreadsheet Notes
    saveRangeSensitivitySpreadsheetNotes();

    // Save Calibration Notes
    saveCalibrationNotes();

    // Save Allocation Notes
    if (!isNil(allocationId)) {
      saveAllocationNotes();
    }
  }, [
    allocationId,
    approaches,
    filesInApproach,
    notesInApproach,
    saveAllocationNotes,
    saveApproachDocuments,
    saveApproachNotes,
    saveCalibrationNotes,
    saveRangeSensitivityDocuments,
    saveRangeSensitivityNotes,
    saveRangeSensitivitySpreadsheetNotes,
  ]);

  // Handle Invalid Approaches
  const handleInvalidTables = useCallback(
    (sheetsWithInvalidCells: ValuationsAreCellsValidValues['sheetsWithInvalidCells']) => {
      const allInvalidTables = [] as string[];

      // Get all Invalid Approaches
      sheetsWithInvalidCells.forEach(currentSheet =>
        currentSheet?.forEach(invalidCell => {
          const { sheet: invalidSheet } = getObjectValue(invalidCell);
          const { name: invalidSheetName = '' } = getObjectValue(invalidSheet);

          const approachTableName = getInvalidSheetTableName(
            invalidSheetName,
            Object.keys(valuationsAllocationTablesNames)
          );

          allInvalidTables.push(approachTableName);

          // Display Invalid Cells
          invalidSheet?.updateAllCellsDisplayConfig();

          // Log Invalid Spreadsheet Cell
          logInvalidCell({
            approachTableName,
            invalidCell,
          });
        })
      );

      const invalidTablesNames = [...new Set(allInvalidTables)].map(tableName => {
        // Is an Approach
        if (tableName in valuationsAllocationTablesNames) {
          const { approachType, id, name } = valuationsAllocationTablesNames[tableName];

          return {
            approachType,
            id,
            name,
          }
        }

        // Valuation Summary
        return {
          approachType: '',
          id: VALUATION_SUMMARY_KEY,
          name: tableName,
        }
      });

      // Set Unique Invalid Tables names
      setInvalidTables(invalidTablesNames);

      // Display Invalid Cells on Console
      displayInvalidCells();
    },
    [displayInvalidCells, logInvalidCell, valuationsAllocationTablesNames]
  );

  const approachesWithInvalidNames = useMemo(
    () =>
      (approaches || [])
        .filter(approach => INVALID_CHARACTERS_REGEX.test(approach.name ?? ''))
        .map(
          approach =>
            ({
              approachType: approach.approach_type,
              id: valuationTabId('approach', getApproachTableName({ approach })),
              name: approach.name,
            }) as SlimApproachTable
        ),
    [approaches]
  );

  const calibrationsWithInvalidNames = useMemo(
    () =>
      calibrations
        .filter(calibration => INVALID_CHARACTERS_REGEX.test(calibration.name ?? ''))
        .map(
          calibration =>
            ({
              approachType: '',
              id: valuationTabId('calibration', getStringValue(calibration.panelId)),
              name: calibration.name,
            })
        ),
    [calibrations]
  );

  const rangeSensitivitiesWithInvalidNames = useMemo(
    () =>
      (valuation.range_sensitivities || [])
        .filter(rs => INVALID_CHARACTERS_REGEX.test(rs.name ?? ''))
        .map(
          rs =>
            ({
              approachType: '',
              id: valuationTabId(
                'range_sensitivity',
                getStringValue((rs as { panelId?: string }).panelId ?? rs.panel_id)
              ),
              name: rs.name,
            })
        ),
    [valuation.range_sensitivities]
  );

  useEffect(() => {
    if (
      approachesWithInvalidNames?.length > 0 ||
      calibrationsWithInvalidNames?.length > 0 ||
      rangeSensitivitiesWithInvalidNames?.length > 0
    ) {
      setInvalidTables([
        ...approachesWithInvalidNames,
        ...calibrationsWithInvalidNames,
        ...rangeSensitivitiesWithInvalidNames,
      ]);
    } else {
      setInvalidTables([]);
    }
  }, [approachesWithInvalidNames, calibrationsWithInvalidNames, rangeSensitivitiesWithInvalidNames]);

  useEffect(() => {
    if (!isLoadingValuationSummary && allocationScenarios?.length > 0 && approaches.length === 0) {
      setInvalidTables(prevInvalidTables => [
        ...prevInvalidTables,
        {
          approachType: '',
          id: MISSING_APPROACHES_FOR_EQUITY_ALLOCATION_ID,
          name: MISSING_APPROACHES_FOR_EQUITY_ALLOCATION,
        },
      ]);
    } else {
      setInvalidTables(prevInvalidTables =>
        prevInvalidTables.filter(table => table.id !== MISSING_APPROACHES_FOR_EQUITY_ALLOCATION_ID)
      );
    }
  }, [allocationScenarios, approaches, isLoadingValuationSummary]);

  // Validate All Spreadsheets
  const allSpreadsheetAreValid = useCallback(() => {
    const { areAllCellsValid, sheetsWithInvalidCells } = areCellsValid(true, true) as ValuationsAreCellsValidValues;

    if (
      approachesWithInvalidNames.length > 0 ||
      calibrationsWithInvalidNames.length > 0 ||
      rangeSensitivitiesWithInvalidNames.length > 0
    ) {
      return false;
    }

    if (allocationScenarios?.length > 0 && approaches.length === 0) {
      return false;
    }

    if (
      areAllCellsValid &&
      approachesWithInvalidNames.length === 0 &&
      calibrationsWithInvalidNames.length === 0 &&
      rangeSensitivitiesWithInvalidNames.length === 0
    ) {
      setInvalidTables([]);
      return true;
    }

    // If invalid Cells are found
    if (sheetsWithInvalidCells) {
      handleInvalidTables(sheetsWithInvalidCells);
    }

    return false;
  }, [
    areCellsValid,
    allocationScenarios,
    approaches,
    approachesWithInvalidNames,
    calibrationsWithInvalidNames,
    rangeSensitivitiesWithInvalidNames,
    handleInvalidTables,
  ]);

  // Validate and Save Valuation
  const validateAndSaveValuationAllocation = useCallback(() => {
    // If all Cells are valid
    if (allSpreadsheetAreValid()) {
      const updatedValuationApproaches = approaches?.map(
        approach =>
          ({
            ...approach,
            financial_statement: getNumberValue(approach?.financial_statement ?? primaryFinancialStatementId),
            reference_for_sibling: getStringValue(approach?.panelId),
          })
      );

      const summaryWorkbook = workbook?.[VALUATION_SUMMARY_KEY];
      const equityValue = Number(
        summaryWorkbook?.cells?.[VALUATION_SUMMARY_SPREADSHEET_WEIGHTED_EQUITY_VALUE_KEY]?.value || '0.0'
      );
      const enterpriseValue = Number(
        summaryWorkbook?.cells?.[VALUATION_SUMMARY_SPREADSHEET_WEIGHTED_ENTERPRISE_VALUE_KEY]?.value || '0.0'
      );

      const roundedEquityValue = Math.round((equityValue + Number.EPSILON) * 100) / 100;
      const roundedEnterpriseValue = Math.round((enterpriseValue + Number.EPSILON) * 100) / 100;

      const valuationToBeSaved = {
        ...valuation,
        market_date: marketDate,
        market_currency: marketCurrency,
        deleted_approaches: deletedApproachesIds,
        valuations_approaches: updatedValuationApproaches,
        weighting_probabilities: weightingProbabilities,
        allocation: {
          ...allocation,
          current_equity_value: roundedEquityValue as unknown as string,
          enterprise_value: roundedEnterpriseValue as unknown as string,
          deleted_allocation_scenario_ids: deletedScenariosIds,
        },
      } as ValuationWithReferences;

      // get the valuation from the related entity
      const relatedEntityValuations = companyInfo?.child_companies
        ?.map(childCompany => {
          const equityAllocationKey = generateEquityAllocationKey(
            (childCompany as RelatedEntity).company_id
          ) as keyof typeof spreadsheets;

          const equityAllocationTableForChildCompany = spreadsheets.find(i => i.name === equityAllocationKey);

          const updatedValuationData = {
            ...equityAllocationTableForChildCompany?.tableData?.valuation,
            name: 'Valuation',
          };

          const updatedAllocationScenarios = (updatedValuationData?.allocation?.allocation_scenarios ?? []).map(
            (scenario: NewAllocationScenario) => ({
              ...scenario,
              name: scenario.name ? scenario.name : 'Empty Scenario',
            })
          );

          const payload = {
            ...updatedValuationData,
            allocation: {
              ...updatedValuationData.allocation,
              name: updatedValuationData.allocation?.name
                ? updatedValuationData.allocation.name
                : `${childCompany.company_name} Allocation`,
              allocation_scenarios: updatedAllocationScenarios,
            },
          };

          return payload;
        })
        .filter((val: ValuationWithReferences) => Boolean(val?.allocation?.weighted_share_values));

      saveValuation({
        cleanUnsavedChanges,
        currentValuationApproaches,
        setArePendingChanges,
        updatedValuation: valuationToBeSaved,
        relatedEntityValuations,
      });

      saveValuationAllocationDocumentsAndNotes();
    }

    setPageStatus(VALIDATED);
  }, [
    spreadsheets,
    companyInfo,
    allSpreadsheetAreValid,
    allocation,
    approaches,
    cleanUnsavedChanges,
    deletedApproachesIds,
    deletedScenariosIds,
    primaryFinancialStatementId,
    saveValuation,
    saveValuationAllocationDocumentsAndNotes,
    valuation,
    weightingProbabilities,
    workbook,
    marketCurrency,
    marketDate,
  ]);

  const {
    changeValuationOwnership,
    showMakeFinalAllocationDialog,
    showPublishAllocationDialog,
    toggleReadyForAudit,
    userCanToggleAuditState,
  } = useValuationStatusManagement({
    allocationVersion,
    user,
    firmId,
    getValuationInfo,
    getVersions,
    isFirmOwned,
    valuation,
    setCurrentAllocationStatus,
    setIsCurrentAllocationFinal,
    setIsCurrentAllocationPublished,
    setPageStatus,
    setValuation,
    showDialog,
  });

  // Create New Valuations Allocation Version
  const handleCreateNewVersion = useCallback(
    async (params: { keep_notes: boolean; name: string }) => {
      const { keep_notes: keepNotes, name } = params;

      // If all Cells are valid
      if (allSpreadsheetAreValid()) {
        const updatedValuationApproaches = approaches?.map(
          approach =>
            ({
              ...approach,
              financial_statement: getNumberValue(approach?.financial_statement ?? primaryFinancialStatementId),
              reference_for_sibling: getStringValue(approach?.panelId),
            }) as ValuationApproachWithReferences
        );

        const valuationToBeDuplicated = {
          ...valuation,
          deleted_approaches: deletedApproachesIds,
          name,
          market_date: marketDate,
          valuations_approaches: updatedValuationApproaches,
          weighting_probabilities: weightingProbabilities,
          allocation: {
            ...allocation,
            version: numberOfVersions, // Number is increased by 1 in the backend
            is_final: false,
            name,
          },
        } as ValuationWithReferences;

        // Explicitly handle keep_notes: ensure it's a boolean, default to true if nullish
        const keepNotesValue = keepNotes ?? true;

        const { valuation: newValuationAllocationVersion } = await createNewVersion({
          valuation: valuationToBeDuplicated,
          keep_notes: keepNotesValue,
        });

        if (!isEmpty(newValuationAllocationVersion?.allocation)) {
          const {
            allocation: newAllocation,
            id: newValuationId,
            market_date: createdMarketDate,
          } = getObjectValue(newValuationAllocationVersion);

          const {
            id: newAllocationId,
            is_final: isFinal,
            is_published: isPublished,
            name: newAllocationName,
            slug,
            version,
          } = getObjectValue(newAllocation);

          const newAllocationVersion = {
            id: newAllocationId,
            is_final: isFinal,
            is_published: isPublished,
            name: newAllocationName,
            slug,
            valuation: newValuationId,
            version,
            market_date: createdMarketDate ?? marketDate,
          } as AllocationName;

          getVersions?.(newAllocationVersion);
        }

        cleanUnsavedChanges();
        setArePendingChanges(false);
      }
    },
    [
      allSpreadsheetAreValid,
      allocation,
      approaches,
      marketDate,
      cleanUnsavedChanges,
      createNewVersion,
      deletedApproachesIds,
      getVersions,
      primaryFinancialStatementId,
      valuation,
      weightingProbabilities,
      numberOfVersions,
    ]
  );

  // Create Valuations Allocation Blank Version
  const createValuationsAllocationBlankVersion = useCallback(async () => {
    if (valuation && allocation) {
      // If all Cells are valid
      if (allSpreadsheetAreValid()) {
        const { id: currentMeasurementDateId, measurement_date: currentMeasurementDate } =
          getObjectValue(companyMeasurementDate);
        const { date } = getObjectValue(currentMeasurementDate);

        // Version number and Ownership will be set in by the Backend API
        const blankValuationAllocation = {
          allocation_id: allocation.id,
          deleted_approaches: [],
          id: valuation.id,
          name: `Firm version - ${date}`,
          market_currency: marketCurrency,
          market_date: marketDate,
          valuations_approaches: [],
          weighting_probabilities: [],
          allocation: {
            allocation_scenarios: [],
            company_measurement_date: currentMeasurementDateId,
            date,
            fund_conclusion: [],
            id: allocation.id,
            is_final: false,
            is_published: false,
            name: `Firm version - ${date}`,
          },
        } as unknown as ValuationWithReferences;

        const { valuation: newValuationAllocationVersion } = await createNewVersion({
          valuation: blankValuationAllocation,
        });

        if (!isEmpty(newValuationAllocationVersion?.allocation)) {
          const {
            allocation: newAllocation,
            id: newValuationId,
            market_date: createdMarketDate,
          } = getObjectValue(newValuationAllocationVersion);

          const {
            id: newAllocationId,
            is_final: isFinal,
            is_published: isPublished,
            name,
            slug,
            version,
          } = getObjectValue(newAllocation);

          // Prefer the persisted market_date so getVersions does not fall back to
          // the measurement date and reset the active market date in the UI.
          const newAllocationVersion = {
            id: newAllocationId,
            is_final: isFinal,
            is_published: isPublished,
            name,
            slug,
            valuation: newValuationId,
            version,
            market_date: createdMarketDate ?? marketDate,
          } as AllocationName;

          getVersions?.(newAllocationVersion);
        }

        cleanUnsavedChanges();
        setArePendingChanges(false);
      }
    }
  }, [
    allSpreadsheetAreValid,
    allocation,
    cleanUnsavedChanges,
    companyMeasurementDate,
    createNewVersion,
    getVersions,
    marketCurrency,
    marketDate,
    valuation,
  ]);

  // Update Percentile Rows
  const updatePercentileRows = useCallback(
    (currentChangedCells: ChangedCell[]) => {
      const cell = currentChangedCells?.[0];

      const isPercentileCell =
        PERCENTILE_ROWS.includes(cell?.alias) && PERCENTILE_ROWS.includes(getStringValue(cell?.key));

      if (isPercentileCell) {
        const { sheet } = getObjectValue(cell);
        const { tableData } = getObjectValue(sheet);
        let { approach } = getObjectValue(tableData);

        // Update GPC or GPT approach
        if (approach && !isEmpty(approach)) {
          const numericValue = Number(cell?.value);

          const { rowConfigGenerator, rowTransformer, specificApproachProperty, updatedApproach } =
            updatePercentileAndGetConfiguration({
              alias: getStringValue(cell?.alias),
              approach,
              updatedValue: getNumberValue(numericValue),
            });

          // Updating the Approach with the Percentile value
          approach = { ...approach, ...updatedApproach };

          const approachProperty = approach[getStringValue(specificApproachProperty) as keyof ValuationsApproach];

          const { gpcOptions } = getGPCOptions(approaches);

          const updatedRowConfig =
            specificApproachProperty === VALUATIONS_APPROACH_GPC_PROPERTY
              ? (rowConfigGenerator as GPCRowConfig)({
                allCompGroups: compGroups,
                approach: approachProperty as ReverseParsedValuationApproachGPC,
                companyName: getStringValue(tableData?.companyName),
                isDisabled: isCurrentValuationDisabled,
                marketDate: tableData?.marketDate ?? marketDate,
                publicCompsByDate: publicCompsByDate ?? undefined,
              })
              : (rowConfigGenerator as GPTRowConfig)({
                allCompGroups: compGroups,
                approach: approachProperty as ReverseParsedValuationApproachGPT,
                companyName: getStringValue(tableData?.companyName),
                gpcOptions,
                isDisabled: isCurrentValuationDisabled,
              });
          const columnsForReset =
            specificApproachProperty === VALUATIONS_APPROACH_GPC_PROPERTY
              ? (rowTransformer as GPCRowTransformer)({
                approach: approachProperty as ValuationApproachGPC,
                financials,
                financialsPeriods: financialPeriods,
                financialStatementId: getNumberValue(approach.financial_statement),
                measurementDate: companyMeasurementDate.measurement_date,
              })
              : (rowTransformer as GPTRowTransformer)({
                approach: approachProperty as ValuationApproachGPT,
                financials,
              });

          cell?.sheet?.reset({
            columns: columnsForReset as ResetParams['columns'],
            rowConfig: updatedRowConfig,
            tableData: { ...tableData, ...getObjectValue(approach) },
          });

          resetConfigurations();
        }
      }
    },
    [
      approaches,
      compGroups,
      isCurrentValuationDisabled,
      marketDate,
      publicCompsByDate,
      financials,
      financialPeriods,
      companyMeasurementDate.measurement_date,
      resetConfigurations,
    ]
  );

  // Update Enterprise Values
  const updateEnterpriseValues = useCallback(
    (updatedCells: ChangedCell[]) => {
      const cellsToBeUpdated = updatedCells
        .filter(cell => cell?.customKey === VALUATIONS_SPREADSHEET_ENTERPRISE_VALUE_KEY)
        .filter(cell => cell?.value !== enterpriseValues?.[getStringValue(cell?.sheet?.name)]);

      if (!isEmpty(cellsToBeUpdated)) {
        const updatedEnterpriseValue = { ...enterpriseValues } as EnterpriseValues;

        cellsToBeUpdated?.forEach(cell => {
          updatedEnterpriseValue[getStringValue(cell?.sheet?.name)] = getStringValue(cell?.value?.toString() ?? '0');
        });

        setEnterpriseValues(updatedEnterpriseValue);
      }
    },
    [enterpriseValues, setEnterpriseValues]
  );

  // Unsaved Changes Dialog
  const showUnsavedChangesDialog = useCallback(
    () =>
      setUnsavedChanges?.({
        content: ConfirmUnsavedChanges,
        wrapper: ConfirmationDialog,
      }),
    [setUnsavedChanges]
  );

  // Get Spreadsheets Names
  const getSpreadsheetsNames = useCallback(
    (configuredSpreadsheets?: ConfiguredSpreadsheets) =>
      Object.entries(getObjectValue(configuredSpreadsheets)).reduce((accumulator, current) => {
        const [key, spreadsheet] = current;
        const { name } = getObjectValue(spreadsheet);

        return {
          ...accumulator,
          [getStringValue(name)]: getStringValue(key),
        };
      }, {} as SpreadsheetsNames),
    []
  );

  // Get Financial Statement Version Name
  const getFinancialStatementName = useCallback(
    (financialStatementId: number) =>
      getStringValue(financialVersions?.find(financialVersion => financialVersion?.id === financialStatementId)?.name),
    [financialVersions]
  );

  const getApproachesByType = useCallback(
    ({ approachType }) => approaches?.filter(approachItem => approachItem?.approach_type === approachType),
    [approaches]
  );

  const getIsGPCInApproach = useCallback((params: GetIsGPCInApproachParams) => {
    const { approachGPCId, approachType, approachesToCheck } = params;

    const approachKey =
      approachType === 'backsolve' ? 'valuations_approach_backsolve' : 'valuations_approach_future_exit';

    return approachesToCheck?.some(
      approach => approach?.[approachKey]?.valuation_approach_gpc?.toString() === approachGPCId
    );
  }, []);

  // Opened Approaches
  const addOpenedApproach = useCallback((approachTableName: string) => {
    setOpenedApproaches(prevState => new Map(prevState.set(approachTableName, true)));
  }, []);

  // Redirect to the New Approach
  const redirectToNewApproach = useCallback(
    (params: RedirectToNewApproachParams) => {
      const { approach, approachTableName, configIndex } = params;

      setTimeout(() => {
        if (
          isNil(approach?.id) &&
          approachesQuantity === configIndex + 1 &&
          !openedApproaches?.get(getStringValue(approachTableName))
        ) {
          setApproachQueryParam(valuationTabId('approach', approachTableName));
          addOpenedApproach(approachTableName);
          scrollToTop();
        }
      }, REDIRECT_TIMEOUT_DELAY);
    },
    [addOpenedApproach, approachesQuantity, openedApproaches, setApproachQueryParam]
  );

  // Valuation Context
  const ValuationContextValues = useMemo(
    () => ({
      approaches,
      backsolveAttributes,
      allocationMethodsOptions,
      updateApproachesScenarioMethods,
      companyMeasurementDate,
      compGroups,
      disableFinancialsSelection: isUpdatedApproachTypeOf(VALUATIONS_EXTERNAL_VALUATION_APPROACH),
      filesInApproach,
      filesInRangeSensitivity,
      financials,
      financialsCurrency: companyInfo?.financials_currency,
      financialsPeriods: financialPeriods,
      financialStatementPeriods: financialFiscalYearPeriods,
      financialStatementPeriodsPerMD: financialPeriodsPerMD,
      financialsVersions: financialVersions,
      gpcAttributes: guidelinePublicCompaniesAttributes,
      calibrationAttributes,
      isChangingFinancialStatement,
      isEditingApproachName,
      isEditingCalibrationName,
      isEditingRangeSensitivityName,
      isEditingOPMCalculatorName,
      marketCurrency,
      marketDate,
      measurementDate,
      notesInApproach,
      notesInCalibration,
      notesInRangeSensitivity,
      spreadsheetNotesInRangeSensitivity,
      onChange: onWorkbookChange,
      publicCompsAttrs: publicCompaniesAttributes,
      publicCompsByDate: publicCompsByDate ?? null,
      resetConfiguration: resetConfigurations,
      selectedFinancialsVersion,
      setAreThereChanges: setArePendingChanges,
      setCashTaxRate,
      setFilesInApproach,
      setFilesInRangeSensitivity,
      setNotesInApproach,
      setNotesInCalibration,
      setNotesInRangeSensitivity,
      setSpreadsheetNotesInRangeSensitivity,
      valuation,
      workbook,
      openOPMCalculator: handleOpenOPMCalculator,
      opmCalculatorPrefillSource,
      clearOPMCalculatorPrefillSource,
      registerApproachFormat,
    }),
    [
      approaches,
      backsolveAttributes,
      allocationMethodsOptions,
      updateApproachesScenarioMethods,
      companyMeasurementDate,
      compGroups,
      isUpdatedApproachTypeOf,
      filesInApproach,
      filesInRangeSensitivity,
      financials,
      companyInfo?.financials_currency,
      financialPeriods,
      financialFiscalYearPeriods,
      financialPeriodsPerMD,
      financialVersions,
      guidelinePublicCompaniesAttributes,
      calibrationAttributes,
      isChangingFinancialStatement,
      isEditingApproachName,
      isEditingCalibrationName,
      isEditingRangeSensitivityName,
      isEditingOPMCalculatorName,
      marketCurrency,
      marketDate,
      measurementDate,
      notesInApproach,
      notesInCalibration,
      notesInRangeSensitivity,
      spreadsheetNotesInRangeSensitivity,
      onWorkbookChange,
      publicCompaniesAttributes,
      publicCompsByDate,
      resetConfigurations,
      selectedFinancialsVersion,
      setCashTaxRate,
      setFilesInRangeSensitivity,
      setNotesInCalibration,
      setNotesInRangeSensitivity,
      setSpreadsheetNotesInRangeSensitivity,
      valuation,
      workbook,
      handleOpenOPMCalculator,
      opmCalculatorPrefillSource,
      clearOPMCalculatorPrefillSource,
      registerApproachFormat,
    ]
  );

  // Synchronize and Update Approaches
  useEffect(() => {
    // This prevents an infinite update loop
    if (shouldSynchronizeApproaches) {
      synchronizeAndUpdateApproaches({
        valuation,
        financialsPeriods: financialPeriods,
        measurementDate,
        companyInfo: getObjectValue(companyInfo),
        setSyncComparisons: setAreSynchronized,
        setApproaches,
        combinedGPCComparisonPerfMetricAttrs,
      });
    }
  }, [
    companyInfo,
    financialPeriods,
    guidelinePublicCompaniesAttributes,
    measurementDate,
    shouldSynchronizeApproaches,
    valuation,
    combinedGPCComparisonPerfMetricAttrs,
  ]);

  // Synchonize Enterprise Values
  useEffect(() => {
    if (data) {
      const currentEnterpriseValues = { ...enterpriseValues };

      Object.entries(data).forEach(([sheetName, sheet]) => {
        const { cells: processedCells } = sheet;

        if (sheetName === VALUATION_SUMMARY_KEY) {
          if (processedCells) {
            sheet?.reverseParse?.();
          }
        } else if (processedCells?.enterprise_value?.value) {
          currentEnterpriseValues[sheetName] = getStringValue(processedCells?.enterprise_value?.value.toString());
        }
      });

      const areEnterpriseValuesSynchronized = Object.entries(currentEnterpriseValues).every(
        ([sheetName, sheetEnterpriseValue]) => sheetEnterpriseValue === enterpriseValues[sheetName]
      );

      // Update Enterprise Values if they are different
      if (!areEnterpriseValuesSynchronized) {
        setEnterpriseValues(currentEnterpriseValues);
      }
    }
  }, [data, enterpriseValues, setEnterpriseValues]);

  // Set Allocation Values
  useEffect(() => {
    updateAllocationValues();
  }, [updateAllocationValues]);

  // Keep the secondary-nav allocation status aligned with the active valuation version.
  useEffect(() => {
    if (!isNil(currentAllocationStatus)) {
      setCurrentAllocationStatus?.(currentAllocationStatus);
    }
  }, [currentAllocationStatus, setCurrentAllocationStatus]);

  // Clear valuation status on unmount so other company pages do not inherit a stale badge.
  useEffect(
    () => () => {
      setCurrentAllocationStatus?.(null);
    },
    [setCurrentAllocationStatus]
  );

  // Updating Synchronized Spreadsheets
  useEffect(() => {
    setSynchronizedSpreadsheets([
      VALUATION_SUMMARY_SPREADSHEET_TABLE_TERMS.tableSlug,
      EQUITY_ALLOCATION_SPREADSHEET_TABLE_TERMS.tableSlug,
    ]);

    return () => {
      // Reset Synchronized Spreadsheets
      setSynchronizedSpreadsheets?.([]);

      // Reset Synchronized By
      setSynchronizedScroll?.({
        synchronizedBy: null,
        synchronizedScrollLeft: 0,
      });
    };
  }, [setSynchronizedScroll, setSynchronizedSpreadsheets]);

  // Save/Update Valuation Allocation
  useEffect(() => {
    if (isValidatingValuation) {
      validateAndSaveValuationAllocation();
    }
  }, [isValidatingValuation, validateAndSaveValuationAllocation]);

  // Display Unsaved Changes Dialog
  useEffect(() => {
    if (arePendingChanges) {
      showUnsavedChangesDialog();
    }
  }, [arePendingChanges, showUnsavedChangesDialog]);

  const changeOwnershipActions = useMemo(
    () => [
      {
        label: CHANGE_OWNERSHIP_LABEL,
        type: 'primary' as const,
        callback: changeValuationOwnership,
      },
    ],
    [changeValuationOwnership]
  );

  const setReadyForAuditActions = useMemo(
    () => [
      {
        label: isReadyForAudit ? REMOVE_READY_FOR_AUDIT : SET_AS_READY_FOR_AUDIT,
        type: 'primary' as const,
        callback: toggleReadyForAudit,
      },
    ],
    [toggleReadyForAudit, isReadyForAudit]
  );

  // Tertiary nav: filtered approach tabs + all tabs (before export filtering)
  const filteredApproachTabs = useMemo(
    () => valuationApproachesTabs.filter(tab => tab.label !== undefined),
    [valuationApproachesTabs]
  );

  // Export-only: one OPM Calculations PDF page per distinct OPM input set, each rendering the
  // calculator purely from its `opm-*` URL params, so no saved calculator row is needed. Not
  // flag-gated here -- flags hydrate async and would race the capture; the dialog gates it.
  const isOPMCalculationsExport = Boolean(
    isExportMode && exportTab === PdfExportTargets.OPM_CALCULATIONS.tab
  );
  const opmCalculationsExportTab = useMemo<Tab | null>(() => {
    if (!isOPMCalculationsExport) {
      return null;
    }
    const exportStub = {
      name: PdfExportTargets.OPM_CALCULATIONS.title,
      valuation: valuation?.id,
      // Keeps the calculator's workbook keys unique and non-empty.
      panelId: OPM_CALCULATIONS_EXPORT_PANEL_ID,
    } as unknown as OPMCalculator;

    return {
      id: PdfExportTargets.OPM_CALCULATIONS.tab,
      pdfExportId: toPdfExportTabId(PdfExportTargets.OPM_CALCULATIONS.tab),
      label: PdfExportTargets.OPM_CALCULATIONS.title,
      content: (
        <OPMCalculatorPanel
          exportTarget={PdfExportTargets.OPM_CALCULATIONS.id}
          isDisabled
          isLoading={false}
          opmCalculator={exportStub}
        />
      ),
    } as Tab;
  }, [isOPMCalculationsExport, valuation?.id]);

  const baseAllTabs = useMemo(
    () => [
      valuationSummaryTab,
      ...calibrationTabs,
      ...rangeSensitivityTabs,
      ...filteredApproachTabs,
      // Appended last: TabsNavigation tracks the active tab and opened tabs by index, so adding or
      // removing an OPM tab must never shift an existing tab's position.
      ...opmCalculatorTabs,
      ...(opmCalculationsExportTab ? [opmCalculationsExportTab] : []),
    ],
    [
      calibrationTabs,
      filteredApproachTabs,
      opmCalculationsExportTab,
      opmCalculatorTabs,
      rangeSensitivityTabs,
      valuationSummaryTab,
    ]
  );

  // Tertiary nav items
  const tertiaryNavItems = useMemo(
    () =>
      baseAllTabs.map(tab => ({
        ...getItemNavigationProps(tab.id ?? null),
        hasError: Boolean(invalidTables.find(t => t.id === tab.id)),
        label: tab.label ?? '',
        showMenu: Boolean(tab.menuItems?.filter(Boolean).length),
        value: tab.id ?? '',
      })),
    [baseAllTabs, getItemNavigationProps, invalidTables]
  );

  const areUnsavedScenarios = useMemo(
    () => allocationScenarios?.some(scenario => scenario?.id === 0) ?? false,
    [allocationScenarios]
  );

  const isSaveDisabled = useMemo(
    () => isCurrentValuationDisabled || isLoadingAllocationScenariosValues || isUpdatingSpreadsheets,
    [isCurrentValuationDisabled, isLoadingAllocationScenariosValues, isUpdatingSpreadsheets]
  );

  const isSaveMenuDisabled = useMemo(
    () => isLoadingAllocationScenariosValues
      || isUpdatingSpreadsheets
      || isValidatingValuation
      || isUpdatingValuation
      || isCreatingNewVersion,
    [
      isCreatingNewVersion,
      isLoadingAllocationScenariosValues,
      isUpdatingSpreadsheets,
      isUpdatingValuation,
      isValidatingValuation,
    ]
  );

  const hasSaveActions = !isEmpty(approaches) || !isEmpty(deletedApproachesIds) || !isAllocationEmpty || Boolean(unrealizedDebt);

  const isPublishedOrFinalActive = !isAllocationEmpty || shouldDisplayWeightedShareValues || Boolean(unrealizedDebt);

  const handleSaveClick = useCallback(() => setPageStatus(VALIDATING), []);

  const activeNavFormat = approachFormats[activeTabValue]?.format ?? format;
  const activeNavDispatch = approachFormats[activeTabValue]?.dispatch ?? formatDispatch;

  const {
    isCurrencyLoading,
    currencyMenuAnchorEl,
    handleCurrencyMenuOpen,
    handleCurrencyMenuClose,
    handleCurrencyChange,
    handleUnitClick,
    currencyDisplayLabel,
    currencyTooltipTitle,
    unitDisplayLabel,
    currencies,
    usedCurrencies,
    showMore,
    showLess,
  } = useCurrencySelector({
    format: activeNavFormat,
    dispatch: activeNavDispatch,
    fallbackMeasurementDateId: measurementDate?.id,
    fallbackSourceCurrency: companyInfo?.captable_currency,
    marketDate,
  });

  const tertiaryNavState = useMemo(
    () => ({
      ariaLabel: 'Valuation approaches',
      items: tertiaryNavItems,
      value: activeTabValue,
      onItemMenuClick: handleTertiaryNavItemMenuClick,
      actions: [
        {
          type: 'currencySelector' as const,
          currency: isCurrencyLoading
            ? <CircularProgress size={14} color="inherit" />
            : currencyDisplayLabel,
          currencyTooltipTitle: isCurrencyLoading ? undefined : currencyTooltipTitle,
          unit: unitDisplayLabel,
          onCurrencyClick: handleCurrencyMenuOpen,
          onUnitClick: handleUnitClick,
          disabled: isCurrencyLoading,
          isVisible: !isExportMode,
        },
        ...(hasSaveActions ? [{
          type: 'splitButton' as const,
          label: SAVE_VALUATION_BUTTON_LABEL,
          variant: 'positive' as const,
          isSaving: isValidatingValuation || isUpdatingValuation || isCreatingNewVersion,
          locked: isCurrentAllocationFinal,
          mainDisabled: isSaveDisabled,
          menuAriaLabel: 'Save options',
          menuDisabled: isSaveMenuDisabled,
          onClick: handleSaveClick,
          onMenuClick: handleSaveMenuClick,
        }] : []),
      ],
    }),
    [
      activeTabValue,
      currencyDisplayLabel,
      currencyTooltipTitle,
      handleCurrencyMenuOpen,
      handleSaveClick,
      handleSaveMenuClick,
      handleTertiaryNavItemMenuClick,
      handleUnitClick,
      hasSaveActions,
      isCurrencyLoading,
      isCurrentAllocationFinal,
      isCreatingNewVersion,
      isExportMode,
      isUpdatingValuation,
      isValidatingValuation,
      isSaveMenuDisabled,
      isSaveDisabled,
      tertiaryNavItems,
      unitDisplayLabel,
    ]
  );

  // Navigation registration is handled by ValuationNavigationRegistrar (rendered below).

  // Secondary nav: version selector + equity value + firm total
  const financialCurrencyFormat = useMemo(
    () => ({
      ...financialRegularFormat,
      style: 'currency',
      validateFloat: false,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }),
    [financialRegularFormat]
  );

  const financialChipExchangeRate = useMemo(
    () => financialRegularFormat?.currency?.exchangeRate?.toString() || '1',
    [financialRegularFormat]
  );

  const formattedEquityValue = useMemo(() => {
    const equityToFormat = isScalarOwnedDraftAndNotStaff ? 0 : currentWeightedEquityValue;
    if (equityToFormat === null || equityToFormat === undefined) {
      return null;
    }
    return returnCurrencyFormattedValueOrNull(equityToFormat, financialCurrencyFormat, financialChipExchangeRate, false);
  }, [currentWeightedEquityValue, financialChipExchangeRate, financialCurrencyFormat, isScalarOwnedDraftAndNotStaff]);

  const formattedFirmTotal = useMemo(() => {
    const firmTotalToFormat =
      isScalarOwnedDraftAndNotStaff || (!shouldDisplayWeightedShareValues && !unrealizedDebt) ? 0 : firmTotal;
    if (firmTotalToFormat === null || firmTotalToFormat === undefined) {
      return null;
    }
    return returnCurrencyFormattedValueOrNull(firmTotalToFormat, financialCurrencyFormat, financialChipExchangeRate, false);
  }, [financialChipExchangeRate, financialCurrencyFormat, firmTotal, isScalarOwnedDraftAndNotStaff, shouldDisplayWeightedShareValues, unrealizedDebt]);

  const secondaryNavActionsNode = useMemo(() => {
    const hasEquityValue = formattedEquityValue !== null;
    const hasFirmTotal = formattedFirmTotal !== null;
    const hasVersions = !!versions?.length;

    if (!hasEquityValue && !hasFirmTotal && !hasVersions) {
      return undefined;
    }

    return (
      <>
        {(hasEquityValue || hasFirmTotal) && (
          <SecondaryNavigationCollapsibleGroup
            ariaLabel="Financial values"
            expandKey="financials"
            icon={
              <AttachMoneyIcon
                fontSize="inherit"
                sx={{ color: theme => theme.palette.navigation.secondary.infoLabel.value.positive }}
              />
            }>
            {formattedEquityValue !== null && (
              <SecondaryNavigationInfoLabel
                data-testid={VALUATION_APPROACHES_TEST_IDS.secondaryNavEquityValue}
                label={EQUITY_VALUE_LABEL}
                value={formattedEquityValue}
                valueTone="positive"
              />
            )}
            {formattedFirmTotal !== null && (
              <SecondaryNavigationInfoLabel
                data-testid={VALUATION_APPROACHES_TEST_IDS.secondaryNavUnrealizedFirmTotal}
                label={UNREALIZED_FIRM_TOTAL_CARD_TITLE}
                value={formattedFirmTotal}
                valueTone="positive"
              />
            )}
          </SecondaryNavigationCollapsibleGroup>
        )}
        {(marketDate || hasVersions) && (
          <SecondaryNavigationCollapsibleGroup
            ariaLabel="Market date and version"
            expandKey="dateVersion"
            icon={
              <HourglassEmptyIcon
                fontSize="inherit"
                sx={{ color: theme => theme.palette.navigation.secondary.infoLabel.value.brand }}
              />
            }>
            {marketDate && (!viewOnlyUser && onOpenMarketDateDialog ? (
              <ValuationMarketDateSelector marketDate={marketDate} onEdit={onOpenMarketDateDialog} />
            ) : (
              <SecondaryNavigationInfoLabel
                data-testid={VALUATION_MARKET_DATE_TEST_IDS.display}
                label="Market Date"
                value={marketDate}
                valueTone="brand"
              />
            ))}
            {versions && versions.length > 0 && (
              <ValuationVersionSelector
                isCreateVersionDisabled={isSaveMenuDisabled}
                onCreateVersion={isScalarOwnedDraftAndNotStaff ? undefined : handleOpenNewVersionDialog}
                onOpenRenameDialog={isScalarOwnedDraftAndNotStaff ? undefined : onOpenRenameVersionDialog}
                onVersionChange={handleVersionChange}
                selectedVersion={allocationVersion}
                versions={versions}
              />
            )}
          </SecondaryNavigationCollapsibleGroup>
        )}
      </>
    );
  }, [
    allocationVersion,
    formattedEquityValue,
    formattedFirmTotal,
    handleOpenNewVersionDialog,
    handleVersionChange,
    isScalarOwnedDraftAndNotStaff,
    isSaveMenuDisabled,
    marketDate,
    onOpenMarketDateDialog,
    onOpenRenameVersionDialog,
    versions,
    viewOnlyUser,
  ]);

  const secondaryNavStatusNode = useMemo(
    () => (
      <>
        <SecondaryNavigationAllocationStatusBadge status={currentAllocationStatus} />
        <SecondaryNavigationReadyForAuditBadge visible={isReadyForAudit && !viewOnlyUser} />
        {/* Set only by a Daily NAV price override; never carried to a copy of the version. */}
        <SecondaryNavigationDailyNavOverrideBadge visible={isDailyNavOverride} />
      </>
    ),
    [currentAllocationStatus, isReadyForAudit, isDailyNavOverride, viewOnlyUser]
  );

  const secondaryNavTitleLeadingNode = useMemo(
    () => <SecondaryNavigationScalarOwnerBadge visible={isScalarOwned} />,
    [isScalarOwned]
  );

  const secondaryNavContribution = useMemo(
    () => ({
      actionsNode: secondaryNavActionsNode,
      status: secondaryNavStatusNode,
      titleLeading: secondaryNavTitleLeadingNode,
    }),
    [secondaryNavActionsNode, secondaryNavStatusNode, secondaryNavTitleLeadingNode]
  );

  // (registration moved to ValuationNavigationRegistrar)

  // Update Enterprise Values and Percentile Rows
  useEffect(() => {
    const currentChangedCells = [...changedCells];

    if (!isEmpty(currentChangedCells)) {
      updateEnterpriseValues(currentChangedCells);
      updatePercentileRows(currentChangedCells);
      setChangedCells([]);
    }
  }, [changedCells, updateEnterpriseValues, updatePercentileRows]);

  // Handle Valuation disabled status
  useEffect(() => {
    setIsCurrentValuationDisabled(
      isCurrentAllocationFinal || isUpdatingValuation || (!isFirmOwned && !isUserStaff) || viewOnlyUser
    );
  }, [isCurrentAllocationFinal, isUpdatingValuation, isFirmOwned, isUserStaff, viewOnlyUser]);

  const isUserAuditorInCurrentFirm = useMemo(
    () => Number(user?.firms_permissions?.find(perm => perm.id === firmId)?.user_firm_role) === AUDITOR_VALUE,
    [firmId, user]
  );

  // Create Valuation Approaches Tabs
  useEffect(() => {
    let createValuationTabsTimer: NodeJS.Timeout;

    if (configurations && cells && workbook) {
      // PDF export v2 loads each page in a hidden iframe; capture treats "no [data-ref]
      // for ~30 display frames" as missing. The default tab-build delay is longer than that
      // window, so approach tabs must schedule immediately in export mode.
      const approachTabsBuildDelay = isExportMode ? 0 : LOADING_TIMEOUT_DELAY;
      createValuationTabsTimer = setTimeout(() => {
        const calibrationTab = configurations
          ?.filter(config => config?.name === CALIBRATION_MODEL_NAME)
          ?.map(config => {
            const { spreadsheets: configuredSpreadsheets, calibration } = config;

            const { inputs, outputs, performance } = getObjectValue(configuredSpreadsheets);
            const { panel_id: panelIdFromAPI, name, panelId: legacyPanelId } = getObjectValue(calibration);

            const panelId = panelIdFromAPI ?? legacyPanelId;

            const menuItems: Tab['menuItems'] = [
              <ApproachActionOption<string, never>
                actionText={EDIT_CALIBRATION_NAME_LABEL}
                argumentsList={[getStringValue(panelId)]}
                key={`${getSlugValue(EDIT_CALIBRATION_NAME_LABEL)}-${getStringValue(panelId)}`}
                onClick={showUpdateCalibrationNameDialog}
              />,
              <RefreshCalibrationOption
                calibration={calibration}
                approaches={approaches}
                closeApproachMenu={handleMenuClose}
                key={`refresh-gpc-${getStringValue(panelId)}`}
                setApproaches={setApproaches}
              />,
              <Divider key={`${getSlugValue(DELETE_CALIBRATION_LABEL)}-divider-${getStringValue(panelId)}`} />,
              <ApproachActionOption<string, never>
                actionText={DELETE_CALIBRATION_LABEL}
                argumentsList={[getStringValue(panelId)]}
                key={`${getSlugValue(DELETE_CALIBRATION_LABEL)}-${getStringValue(panelId)}`}
                onClick={showDeleteCalibrationDialog}
              />,
            ];

            const calibrationComponent = {
              id: valuationTabId('calibration', getStringValue(panelId)),
              pdfExportId: toPdfExportTabId('calibration', panelId),
              label: name,
              content: (
                <Calibration
                  spreadsheets={{
                    calibration_inputs: inputs,
                    calibration_outputs: outputs,
                    calibration_performance: performance,
                  }}
                  workbook={workbook}
                  onChange={handleOnSpreadsheetChange}
                  approaches={approaches}
                  financials={financials}
                  company={companyInfo}
                  financialStatementsList={financialStatementsList}
                  calibration={calibration}
                  isDisabled={isCurrentValuationDisabled}
                />
              ),
              menuItems,
            } as Tab;

            return calibrationComponent;
          });

        setCalibrationTabs(calibrationTab);

        const rangeSensitivityTab = configurations
          ?.filter(config => config?.name === RANGE_SENSITIVITY_MODEL_NAME)
          ?.map(config => {
            const { spreadsheets: rsSpreadsheets, rangeSensitivity } = config;
            const rs = getObjectValue(rangeSensitivity);
            const panelId = getStringValue((rs as { panelId?: string }).panelId ?? rs.panel_id);
            const rsWorkbookKey = getRangeSensitivityWorkbookKey(panelId);

            const rsMenuItems: Tab['menuItems'] = [
              <ApproachActionOption<string, never>
                actionText={EDIT_RANGE_SENSITIVITY_NAME_LABEL}
                argumentsList={[panelId]}
                key={`${getSlugValue(EDIT_RANGE_SENSITIVITY_NAME_LABEL)}-${panelId}`}
                onClick={showUpdateRangeSensitivityNameDialog}
              />,
              <Divider key={`${getSlugValue(DELETE_RANGE_SENSITIVITY_LABEL)}-divider-${panelId}`} />,
              <ApproachActionOption<string, never>
                actionText={DELETE_RANGE_SENSITIVITY_LABEL}
                argumentsList={[panelId]}
                key={`${getSlugValue(DELETE_RANGE_SENSITIVITY_LABEL)}-${panelId}`}
                onClick={showDeleteRangeSensitivityDialog}
              />,
            ];

            const rsSpreadsheetsNames = getSpreadsheetsNames(rsSpreadsheets);
            const rsWorkbookSpreadsheets = getSpreadsheetsFromWorkbook({
              spreadsheetsNames: rsSpreadsheetsNames,
              workbook,
            });

            return {
              id: valuationTabId('range_sensitivity', panelId),
              pdfExportId: toPdfExportTabId('range_sensitivity', panelId),
              label: rs.name,
              content: (
                <RangeSensitivityPanel
                  rangeSensitivity={rs}
                  spreadsheetWorkbookKey={rsWorkbookKey}
                  allocation={getObjectValue(valuation?.allocation)}
                  securitiesCount={securities?.length ?? 0}
                  isDisabled={isCurrentValuationDisabled}
                  isLoading={false}
                  isSaving={isUpdatingValuation}
                  onChange={handleOnSpreadsheetChange}
                  spreadsheets={
                    isEmpty(rsWorkbookSpreadsheets)
                      ? (rsSpreadsheets ?? ({} as ConfiguredSpreadsheets))
                      : rsWorkbookSpreadsheets
                  }
                  workbook={workbook}
                />
              ),
              menuItems: rsMenuItems,
            } as Tab;
          });

        setRangeSensitivityTabs(rangeSensitivityTab);

        // No `pdfExportId`: saved calculators are never captured as-is. The PDF export renders
        // the export-only OPM Calculations tab instead, once per distinct OPM input set.
        const opmCalculatorTab = configurations
          ?.filter(config => config?.name === OPM_CALCULATOR_MODEL_NAME)
          ?.map(config => {
            const { opmCalculator } = config;
            const opm = getObjectValue(opmCalculator);
            const panelId = getOPMCalculatorPanelId(opm);

            const opmMenuItems: Tab['menuItems'] = [
              <ApproachActionOption<string, never>
                actionText={EDIT_OPM_CALCULATOR_NAME_LABEL}
                argumentsList={[panelId]}
                key={`${getSlugValue(EDIT_OPM_CALCULATOR_NAME_LABEL)}-${panelId}`}
                onClick={showUpdateOPMCalculatorNameDialog}
              />,
              <Divider key={`${getSlugValue(DELETE_OPM_CALCULATOR_LABEL)}-divider-${panelId}`} />,
              <ApproachActionOption<string, never>
                actionText={DELETE_OPM_CALCULATOR_LABEL}
                argumentsList={[panelId]}
                key={`${getSlugValue(DELETE_OPM_CALCULATOR_LABEL)}-${panelId}`}
                onClick={showDeleteOPMCalculatorDialog}
              />,
            ];

            return {
              id: valuationTabId('opm_calculator', panelId),
              // `*` marks a calculator that is new or has edits not yet saved.
              label: unsavedOPMCalculatorPanelIds.has(panelId) ? `${getStringValue(opm.name)}*` : opm.name,
              content: (
                <OPMCalculatorPanel
                  isDisabled={isCurrentValuationDisabled}
                  isLoading={false}
                  isSaving={isUpdatingValuation}
                  onInputsChange={handleUpdateOPMCalculatorInputs}
                  opmCalculator={opm}
                />
              ),
              menuItems: opmMenuItems,
            } as Tab;
          });

        setOPMCalculatorTabs(opmCalculatorTab);

        const filteredConfigs = configurations.filter(filterApproaches);

        const valuationTabs = filteredConfigs
          .sort(sortApproaches)
          .map((config, configIndex) => {
            const {
              spreadsheets: configuredSpreadsheets,
              approach,
              component,
              enterpriseValueReference: sheetName,
              calibration,
            } = config;

            const {
              financial_statement: approachFinancialStatementId,
              name: approachName,
              panelId: approachPanelId,
            } = getObjectValue(approach);

            // Used in the redirect to new calibrations or approaches
            const tableName =
              calibration && isNil(calibration?.approach?.valuations_approach_gpc)
                ? config?.calibration?.panelId?.replaceAll('-', '_')
                : getApproachTableName({ approach });

            const approachTableName = getStringValue(tableName);

            const spreadsheetsNames = getSpreadsheetsNames(configuredSpreadsheets);

            const approachSpreadsheets = getSpreadsheetsFromWorkbook({ spreadsheetsNames, workbook });

            const backsolveApproaches = getApproachesByType({
              approachType: VALUATIONS_BACKSOLVE_APPROACH,
            });

            const futureExitApproaches = getApproachesByType({
              approachType: VALUATIONS_FUTURE_EXIT_APPROACH,
            });

            // External Valuation
            const isExternalValuation = isApproachTypeOf({ approach, type: VALUATIONS_EXTERNAL_VALUATION_APPROACH });

            // Guideline Public Companies
            const isGuidelinePublicCompaniesApproach = isApproachTypeOf({
              approach,
              type: VALUATIONS_PUBLIC_COMPANIES_APPROACH,
            });
            const shouldShowRefreshGPCOption = isGuidelinePublicCompaniesApproach && !isUserAuditorInCurrentFirm;

            const isBacksolveApproach = isApproachTypeOf({ approach, type: VALUATIONS_BACKSOLVE_APPROACH });
            const backsolveSubtypeValue = approach?.valuations_approach_backsolve?.backsolve_type;
            const isSTorMFMApproach
              = isBacksolveApproach
              && (backsolveSubtypeValue === 'secondary_transactions'
                || backsolveSubtypeValue === 'mutual_fund_marks');
            const shouldShowRefreshSourceRowsOption
              = isSTorMFMApproach && !isUserAuditorInCurrentFirm && (measurementDate?.is_open ?? false);

            const approachGPCId = getStringValue(approach?.valuations_approach_gpc?.id?.toString());

            const isGPCInMarketAdjustment = getIsGPCInApproach({
              approachGPCId,
              approachesToCheck: backsolveApproaches,
              approachType: 'backsolve',
            });

            const isGPCInFutureExit = getIsGPCInApproach({
              approachGPCId,
              approachesToCheck: futureExitApproaches,
              approachType: 'futureExit',
            });

            // Approaches Values - Equity and Enterprise Value
            const currentEnterpriseValue = Number(getNumberValue(enterpriseValues?.[getStringValue(sheetName)]));
            const totalDebt = Number(getNumberValue(financials?.total_debt));
            const totalCash = Number(getNumberValue(financials?.total_cash_equivalents));

            const isCapTableCurrencyApproach = VALUATIONS_CAP_TABLE_CURRENCY_APPROACHES.includes(
              getStringValue(approach?.approach_type)
            );

            const currentEquityValue = calculateEquityValue({
              companyExchangeRate: getNumberValue(companyExchangeRate),
              enterpriseValue: currentEnterpriseValue,
              isCapTableCurrencyApproach,
              totalCash,
              totalDebt,
            });

            const approachValuesFormat = {
              ...(isCapTableCurrencyApproach ? capTableRegularFormat : financialRegularFormat),
              style: 'currency',
              validateFloat: true,
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            };

            const approachValuesExchangeRate = getStringValue(
              isCapTableCurrencyApproach ? companyExchangeRate : financialExchangeRate
            );

            const Component = component ?? DefaultPanelComponent;

            // Approach Menu Items
            const menuItems: Tab['menuItems'] = [
              <ApproachActionOption<string, never>
                actionText={EDIT_APPROACH_NAME_LABEL}
                argumentsList={[getStringValue(approachPanelId)]}
                key={`${getSlugValue(EDIT_APPROACH_NAME_LABEL)}-${getStringValue(approachPanelId)}`}
                onClick={showUpdateApproachNameDialog}
              />,
              // Add Change Financial Statement option
              !isExternalValuation && financialVersionQuantity > 1 ? (
                <ApproachActionOption<string, never>
                  actionText={CHANGE_FINANCIAL_STATEMENT_LABEL}
                  argumentsList={[getStringValue(approachPanelId)]}
                  key={`${getSlugValue(CHANGE_FINANCIAL_STATEMENT_LABEL)}-${getStringValue(approachPanelId)}`}
                  onClick={showUpdateFinancialStatementDialog}
                />
              ) : null,
              <Divider key={`${getSlugValue(DELETE_APPROACH_LABEL)}-divider-${getStringValue(approachPanelId)}`} />,
              <ApproachActionOption<string, never>
                actionText={DELETE_APPROACH_LABEL}
                argumentsList={[getStringValue(approachPanelId)]}
                disabled={isGPCInMarketAdjustment || isGPCInFutureExit}
                key={`${getSlugValue(DELETE_APPROACH_LABEL)}-${getStringValue(approachPanelId)}`}
                tooltipMessage={VALUATIONS_CANNOT_DELETE_GPC}
                onClick={showDeleteApproachDialog}
                shouldDisplayTooltip={isGPCInMarketAdjustment || isGPCInFutureExit}>
                {isGPCInMarketAdjustment || isGPCInFutureExit ? <InfoIcon className={classes.gpcInUse} /> : <></>}
              </ApproachActionOption>,
              // Add Refresh GPC Data option. Auditors should not be allowed nor prompted to do so.
              shouldShowRefreshGPCOption ? (
                <Divider key={`refresh-gpc-divider-${getStringValue(approachPanelId)}`} />
              ) : null,
              shouldShowRefreshGPCOption ? (
                <RefreshGPCOption
                  approach={approach}
                  approaches={approaches}
                  closeApproachMenu={handleMenuClose}
                  key={`refresh-gpc-${getStringValue(approachPanelId)}`}
                  setApproaches={setApproaches}
                  marketDate={marketDate}
                  marketCurrency={marketCurrency}
                />
              ) : null,

              // Refresh Source Rows (Secondary Transactions / Mutual Fund Marks)
              shouldShowRefreshSourceRowsOption ? (
                <Divider key={`refresh-source-rows-divider-${getStringValue(approachPanelId)}`} />
              ) : null,
              shouldShowRefreshSourceRowsOption ? (
                <RefreshBacksolveSourceRowsOption
                  approach={approach}
                  approaches={approaches}
                  closeApproachMenu={handleMenuClose}
                  isCurrentMeasurementDate={measurementDate?.is_open ?? false}
                  key={`refresh-source-rows-${getStringValue(approachPanelId)}`}
                  setApproaches={setApproaches}
                />
              ) : null,

              // Approach Values
              <Divider key={`approach-values-divider-${getStringValue(approachPanelId)}`} />,
              <ApproachMenuInfoSection
                key={`approach-values-${getStringValue(approachPanelId)}`}
                role="presentation">
                {renderApproachMenuInfoItem(
                  FINANCIAL_STATEMENT_LABEL,
                  getApproachFinancialStatementInfoValue(
                    getFinancialStatementName(getNumberValue(approachFinancialStatementId))
                  ),
                  `financial-statement-${getStringValue(approachPanelId)}`
                )}
                {renderApproachMenuInfoItem(
                  ENTERPRISE_VALUE_LABEL,
                  returnCurrencyFormattedValueOrNull(
                    currentEnterpriseValue,
                    approachValuesFormat,
                    approachValuesExchangeRate,
                    isCapTableCurrencyApproach
                  ),
                  `enterprise-value-${getStringValue(approachPanelId)}`
                )}
                {renderApproachMenuInfoItem(
                  EQUITY_VALUE_LABEL,
                  returnCurrencyFormattedValueOrNull(
                    currentEquityValue,
                    approachValuesFormat,
                    approachValuesExchangeRate,
                    isCapTableCurrencyApproach
                  ),
                  `equity-value-${getStringValue(approachPanelId)}`
                )}
              </ApproachMenuInfoSection>,
            ];

            // Approach Tab
            const approachTab = {
              id: valuationTabId('approach', approachTableName),
              pdfExportId: toPdfExportTabId('approach', approach?.approach_type, approach?.id ?? approachPanelId),
              label: approachName,
              content: (
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore: Component is not typed
                <Component
                  approach={approach}
                  approaches={approaches}
                  capTableList={capTableVersions}
                  enterpriseValueMap={enterpriseValues}
                  financials={financials}
                  isDisabled={isCurrentValuationDisabled}
                  isSaving={isUpdatingValuation}
                  measurementDate={measurementDate}
                  name={sheetName}
                  onChange={handleOnSpreadsheetChange}
                  setEnterpriseValueMap={setEnterpriseValues}
                  spreadsheets={isEmpty(approachSpreadsheets) ? configuredSpreadsheets : approachSpreadsheets}
                  workbook={workbook}
                />
              ),
              menuItems,
            } as Tab;

            // Redirect to the new Approach Tab
            redirectToNewApproach({ approach, approachTableName, configIndex });

            return approachTab;
          })
          .filter(Boolean);

        setValuationApproachesTabs(valuationTabs);
        setAreTabsSynchronized(true);
      }, approachTabsBuildDelay);
    }

    return () => clearTimeout(createValuationTabsTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    approaches,
    capTableRegularFormat,
    capTableVersions,
    cells,
    classes,
    companyExchangeRate,
    companyInfo,
    configurations,
    enterpriseValues,
    financialExchangeRate,
    financialRegularFormat,
    financialStatementsList,
    financialVersionQuantity,
    financials,
    getApproachesByType,
    getFinancialStatementName,
    getIsGPCInApproach,
    getSpreadsheetsNames,
    handleOnSpreadsheetChange,
    isExportMode,
    isCurrentValuationDisabled,
    isUpdatingValuation,
    measurementDate,
    redirectToNewApproach,
    setCalibrationTabs,
    setEnterpriseValues,
    showDeleteApproachDialog,
    showDeleteCalibrationDialog,
    showDeleteRangeSensitivityDialog,
    showUpdateApproachNameDialog,
    showUpdateFinancialStatementDialog,
    showUpdateRangeSensitivityNameDialog,
    spreadsheets,
    valuation.calibrations,
    valuation.range_sensitivities,
    workbook,
    handleDeleteRangeSensitivity,
    setRangeSensitivityTabs,
    showDeleteOPMCalculatorDialog,
    showUpdateOPMCalculatorNameDialog,
    valuation.opm_calculators,
    handleDeleteOPMCalculator,
    handleUpdateOPMCalculatorInputs,
    setOPMCalculatorTabs,
    unsavedOPMCalculatorPanelIds,
  ]);

  if (!areSynchronized && !isValuationEmpty) {
    return <LoadingSection />;
  }

  if (isScalarOwnedDraftAndNotStaff) {
    return (
      <>
        <ValuationNavigationRegistrar
          secondaryNavContribution={secondaryNavContribution}
          tertiaryNavState={null}
        />
        <MessageBox
          action={<CreateNewVersionButton onClickHandler={createValuationsAllocationBlankVersion} />}
          fullWidth={false}
          title={SCALAR_OWNED_DRAFT_MESSAGE}
          titleStyleOverride={{ whiteSpace: 'pre-line' }}
        />
      </>
    );
  }

  // Display empty Valuations message if there are no Financial Periods
  if (!financialFiscalYearPeriods) {
    return (
      <EmptyTableMessage
        component={<RedirectToFinancialButton measurementDateSlug={getStringValue(measurementDate?.slug)} />}
        tableTerms={VALUATIONS_SPREADSHEET_TABLE_TERMS}
        tagline={VALUATIONS_MISSING_FINANCIAL_STATEMENT_TAGLINE}
        title={VALUATIONS_MISSING_FINANCIAL_STATEMENT_TITLE}
      />
    );
  }
  // Export mode filters to the target tab while normal Prism renders tabs through tertiary nav.
  const displayTabs =
    isExportMode && exportTab
      ? baseAllTabs.filter(tab => (tab.pdfExportId ?? tab.id)?.startsWith(exportTab))
      : baseAllTabs;

  // PDF export iframes omit the normal `tab=` URL param. With `mountOnActive`, only the
  // active tab mounts, so choose the most specific matching export tab.
  const exportNavigationTab =
    isExportMode && exportTab && displayTabs.length > 0
      ? maxBy(displayTabs, t => (t.pdfExportId ?? t.id ?? '').length)
      : undefined;
  const exportNavigationActiveTabId = exportNavigationTab?.id ? getStringValue(exportNavigationTab.id) : undefined;

  // Approach tab context menu is driven by the Prism tertiary nav per-tab menu button.
  const activeTabMenuItems = baseAllTabs.find(tab => tab.id === menuTabId)?.menuItems ?? [];

  let updateApproachDialogTitle = EDIT_APPROACH_NAME_LABEL;
  if (rangeSensitivityToUpdate) {
    updateApproachDialogTitle = EDIT_RANGE_SENSITIVITY_NAME_LABEL;
  } else if (calibrationToUpdate) {
    updateApproachDialogTitle = EDIT_CALIBRATION_NAME_LABEL;
  } else if (opmCalculatorToUpdate) {
    updateApproachDialogTitle = EDIT_OPM_CALCULATOR_NAME_LABEL;
  }

  const readyForAuditLabel = isReadyForAudit ? REMOVE_READY_FOR_AUDIT : READY_FOR_AUDIT;

  return (
    <>
      <ValuationNavigationRegistrar
        secondaryNavContribution={secondaryNavContribution}
        tertiaryNavState={tertiaryNavState}
      />
      <CurrencyMenu
        anchorEl={currencyMenuAnchorEl}
        data-testid="valuation-currency-menu"
        currencies={currencies}
        isCurrencyLoading={isCurrencyLoading}
        onClose={handleCurrencyMenuClose}
        onCurrencyChange={handleCurrencyChange}
        showMore={showMore}
        showLess={showLess}
        usedCurrencies={usedCurrencies}
      />
      <Grid className={`${VALUATIONS_SLUG}-container`}>
        <ValuationContext.Provider value={ValuationContextValues}>
          <ApproachRegistryContext.Provider value={approachRegistryValue}>
            <WorkbookContext.Provider value={workbookContextValue}>
              {/* Render Valuation Approaches Tabs — tab bar hidden in normal mode and shown only for export. */}
              <TabsNavigation
                updateUrlOnTabChange
                handleMenuClose={handleMenuClose}
                handleMenuOpen={handleMenuOpen}
                hideTabBar={!isExportMode}
                invalidTables={invalidTables}
                isDisabled={isCurrentValuationDisabled}
                menuAnchorEl={menuAnchorEl}
                mountOnActive
                activeTabId={exportNavigationActiveTabId}
                tabs={displayTabs}>
                <>
                  {/* Display Decision Message */}
                  {displayDecisionAlerts?.()}

                  {/* Invalid Approaches Message */}
                  {Boolean(invalidTables?.length) && <InvalidTablesMessage invalidTables={invalidTables} />}

                  {/* Unlock Final Allocation Message */}
                  {isCurrentAllocationFinal && isCurrentValuationDisabled && (
                    <AllocationFinalMessage
                      viewOnlyUser={viewOnlyUser}
                      setIsDisabled={setIsCurrentValuationDisabled}
                      singleAlert
                      tableTerms={{ tableName: VALUATIONS_SPREADSHEET_TABLE_TERMS.tableName }}
                      finalAllocations={[]}
                    />
                  )}

                  {/* Final Allocation Message */}
                  {isCurrentAllocationFinal && !isCurrentValuationDisabled && (
                    <Alert isAlertVisible>{VALUATIONS_FINAL_VALUATION_LOCKED_MESSAGE}</Alert>
                  )}
                </>
              </TabsNavigation>

              {/* Approach tab context menu (driven by tertiary nav onItemMenuClick) */}
              <PrimaryNavigationMenu
                anchorEl={tabMenuAnchorEl}
                data-testid={VALUATION_APPROACHES_TEST_IDS.approachMenu}
                horizontalPlacement="right"
                menuMaxWidth={320}
                menuMinWidth={180}
                menuWidth="content"
                onClose={handleMenuClose}
                open={Boolean(tabMenuAnchorEl)}
                zoomLevel={zoomLevel}
                MenuListProps={{ disableListWrap: true }}>
                {activeTabMenuItems}
              </PrimaryNavigationMenu>

              {/* Save options dropdown */}
              <PrimaryNavigationMenu
                anchorEl={saveMenuAnchorEl}
                horizontalPlacement="right"
                menuMaxWidth={280}
                menuMinWidth={180}
                menuWidth="content"
                onClose={handleSaveMenuClose}
                open={Boolean(saveMenuAnchorEl)}
                zoomLevel={zoomLevel}
                MenuListProps={{ disableListWrap: true }}>
                {isAllocationValid && !isCurrentAllocationPublished && !isCurrentAllocationFinal && (
                  <PrimaryNavigationMenuItem
                    disabled={areUnsavedScenarios || !isPublishedOrFinalActive}
                    icon={<PublishOutlined />}
                    onClick={() => {
                      handleSaveMenuClose();
                      showPublishAllocationDialog();
                    }}>
                    {PUBLISH_ALLOCATION_BUTTON_LABEL}
                  </PrimaryNavigationMenuItem>
                )}
                {isAllocationValid && !isCurrentAllocationFinal && isCurrentAllocationPublished && (
                  <PrimaryNavigationMenuItem
                    disabled={areUnsavedScenarios || !isPublishedOrFinalActive}
                    icon={<CheckCircleOutline />}
                    onClick={() => {
                      handleSaveMenuClose();
                      showMakeFinalAllocationDialog();
                    }}>
                    {MAKE_FINAL_ALLOCATION_BUTTON_LABEL}
                  </PrimaryNavigationMenuItem>
                )}
                {isFirmOwned && isUserStaff && (
                  <PrimaryNavigationMenuItem
                    icon={<SwapHorizOutlined />}
                    onClick={() => {
                      handleSaveMenuClose();
                      showDialog({
                        wrapper: ConfirmationDialog,
                        wrapperProps: { title: CHANGE_VALUATION_OWNERSHIP_LABEL },
                        content: ValuationOwnershipConfirmation,
                        actions: changeOwnershipActions,
                      });
                    }}>
                    {CHANGE_TO_SCALAR_VALUATION_LABEL}
                  </PrimaryNavigationMenuItem>
                )}
                {!isFirmOwned && isUserStaff && (
                  <PrimaryNavigationMenuItem
                    icon={<SwapHorizOutlined />}
                    onClick={() => {
                      handleSaveMenuClose();
                      showDialog({
                        wrapper: ConfirmationDialog,
                        wrapperProps: { title: CHANGE_VALUATION_OWNERSHIP_LABEL },
                        content: ValuationOwnershipConfirmation,
                        actions: changeOwnershipActions,
                      });
                    }}>
                    {GIVE_OWNERSHIP_TO_FIRM_LABEL}
                  </PrimaryNavigationMenuItem>
                )}
                {isAllocationValid && userCanToggleAuditState && (
                  <PrimaryNavigationMenuItem
                    icon={<AssignmentTurnedInOutlined />}
                    onClick={() => {
                      handleSaveMenuClose();
                      showDialog({
                        wrapper: ConfirmationDialog,
                        wrapperProps: { title: readyForAuditLabel },
                        content: () => <SetReadyForAuditConfirmation currentReadinessStatus={isReadyForAudit} />,
                        actions: setReadyForAuditActions,
                      });
                    }}>
                    {readyForAuditLabel}
                  </PrimaryNavigationMenuItem>
                )}
              </PrimaryNavigationMenu>

              {/* New Valuations Allocation Version */}
              <NewVersionForm
                defaultValues={DEFAULT_VALUES}
                onClose={handleCloseNewVersionDialog}
                nextVersionNumber={numberOfVersions + 1}
                onSave={handleCreateNewVersion}
                open={openNewVersionDialog}
              />
            </WorkbookContext.Provider>
          </ApproachRegistryContext.Provider>
          {/* Add Scenario/Approach Dialog */}
          <AddScenarioApproachDialog
            disableAddApproachButton={isCurrentValuationDisabled || isLoadingValuationSummary}
            disableAddScenarioButton={areCapTableSecuritiesInvalid || isFundOwnershipEmpty || approaches.length === 0}
            handleAddApproach={handleAddApproach}
            handleAddScenario={handleAddScenario}
            handleCloseAddApproachDialog={handleCloseAddApproachDialog}
            handleOpenAddApproachDialog={handleOpenAddApproachDialog}
            openAddApproachDialog={openAddApproachDialog}
            approaches={approaches}
            hasOPMConsumer={hasOPMConsumer}
          />

          {/* Update Approach Dialog */}
          <ApproachDialog
            approach={approachToUpdate}
            calibration={calibrationToUpdate}
            rangeSensitivity={rangeSensitivityToUpdate}
            opmCalculator={opmCalculatorToUpdate}
            editMode={false}
            handleOnClose={handleCloseUpdateApproachDialog}
            handleOnSave={handleUpdateApproach}
            measurementDate={getStringValue(measurementDate?.date?.toString())}
            open={openUpdateApproachDialog}
            shouldDisplayGPCOptions={isUpdatedApproachTypeOf(VALUATIONS_PUBLIC_COMPANIES_APPROACH)}
            isEquityOptionAvailable={shouldDisplayEquityOption(approachToUpdate)}
            title={updateApproachDialogTitle}
          />
        </ValuationContext.Provider>
        {getDisconnectDialog?.()}
      </Grid>
    </>
  );
};

export default ValuationApproaches;
